/**
 * Từ điển tiếng Anh — sinh ra phản chiếu `vi.ts`, cùng thứ tự, cùng đầu mục.
 *
 * ── Vì sao phải ĐỦ, không được thiếu (OQ-290) ──────────────────────────────
 *
 * Chú thích cũ ở đây viết: *"khoá thiếu sẽ rơi về tiếng Việt, nên bản dịch dở
 * dang không bao giờ hiện khoá thô cho người dùng"*. Câu ấy đúng về kỹ thuật
 * và sai về hệ quả: nó biến một tệp thiếu **940 trên 1287 khoá** thành thứ
 * trông vẫn chạy được.
 *
 * > Cơ chế rơi về ngôn ngữ gốc là **lưới an toàn**, không phải kế hoạch. Nó
 * > che đúng thứ cần lộ ra: người dùng chuyển sang English, thấy tiếng Việt,
 * > và không có gì trong hệ thống coi đó là lỗi.
 *
 * Nay `i18n-day-du.test.ts` đòi mọi khoá của `vi.ts` phải có mặt ở đây. Thêm
 * một khoá tiếng Việt mà quên bản tiếng Anh thì đỏ ngay, chứ không đợi bảy mốc.
 */
export const en: Record<string, string> = {
  // ── Chung ────────────────────────────────────────────────
  'app.name': 'DNSHub',
  'app.tagline': 'Cloudflare domain management',
  'error.network':
    'Cannot reach the server. Check your connection and try again — if it persists, the server may be down.',
  'error.unexpected': 'An unexpected error occurred.',
  'common.save': 'Save',
  'common.saveChanges': 'Save changes',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.on': 'on',
  'common.off': 'off',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.duplicate': 'Duplicate',
  'common.create': 'Create',
  'common.confirm': 'Confirm',
  'common.retry': 'Retry',
  'common.undo': 'Undo',
  'common.refresh': 'Refresh',
  'common.view': 'View',
  'common.loading': 'Loading…',
  'common.processing': 'Working…',
  'common.search': 'Search',
  'common.showPassword': 'Show password',
  'common.hidePassword': 'Hide password',
  'common.clearSearch': 'Clear search',
  'common.copy': 'Copy',
  'common.closeToast': 'Dismiss notification',
  'common.copied': 'Copied',
  'common.back': 'Back',
  'common.backToOverview': 'Back to overview',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.none': 'None',
  'common.optional': 'optional',
  'common.required': 'required',
  'common.download': 'Download',
  'common.print': 'Print',
  'common.enabled': 'Enabled',
  'common.disabled': 'Disabled',
  'common.system': 'System',
  'common.default': 'Default',
  'common.never': 'Never',
  'common.unknown': 'Unknown',

  // ── Trạng thái UI (docs/08 §8.5) ─────────────────────────
  'state.empty.title': 'No data yet',
  'state.error.title': 'Could not load data',
  'state.error.body': 'Something went wrong talking to the server. You can try again.',
  'state.forbidden.title': 'You do not have access to this area',
  'state.forbidden.body': 'Contact an administrator if you need access.',
  'state.notFound.title': 'Page not found',
  'state.notFound.body': 'That address does not exist or has changed.',

  // ── Điều hướng ───────────────────────────────────────────
  'nav.group.operate': 'Operations',
  'nav.group.admin': 'Administration',
  'nav.group.account': 'Account',
  'nav.overview': 'Overview',
  'nav.domains': 'Domains',
  'nav.subdomains': 'Sub-domains',
  'subdomains.title': 'Sub-domains',
  'subdomains.lede':
    'Carve out a branch of a domain and hand it to someone else, without opening the whole DNS zone.',
  'subdomains.add': 'Create sub-domain',
  'subdomains.addTitle': 'Create a sub-domain',
  'subdomains.created': 'Sub-domain created.',
  'subdomains.count': '{n} sub-domains',
  'subdomains.recordCount': '{n} records',
  'subdomains.searchPlaceholder': 'Search sub-domains…',
  'subdomains.emptyTitle': 'No sub-domains yet',
  'subdomains.emptyBody':
    'Create a sub-domain to hand one branch to someone else, for example blog.example.com.',
  'subdomains.col.fqdn': 'Sub-domain',
  'subdomains.col.domain': 'Parent domain',
  'subdomains.col.records': 'Records',
  'subdomains.col.shares': 'Shared',
  'subdomains.col.relation': 'Access',
  'subdomains.col.created': 'Created',
  'subdomains.col.actions': 'Actions',
  'subdomains.relation.domain': 'Via domain',
  'subdomains.relation.granted': 'Shared with you',
  'subdomains.field.domain': 'Parent domain',
  'subdomains.field.domainPlaceholder': 'Select a domain…',
  'subdomains.field.label': 'Sub-domain name',
  'subdomains.field.labelHint': 'A single label with no dots — for example blog.',
  'subdomains.field.labelHintWith': 'This creates {fqdn}',
  'subdomains.noDomainCanCreate':
    'None of your domains allow creating sub-domains. You need the Create sub-domain permission on that domain.',
  'subdomains.detail.lede': 'A branch of {domain}. You can only manage records inside this branch.',
  'subdomains.tab.records': 'DNS records',
  'subdomains.tab.sharing': 'Sharing',
  'subdomains.record.add': 'Add record',
  'subdomains.record.addTitle': 'Add a record in this branch',
  'subdomains.record.created': 'Record added.',
  'subdomains.record.deleted': 'Record deleted.',
  'subdomains.record.emptyTitle': 'No records in this branch yet',
  'subdomains.record.emptyBody': 'Add the first record for {fqdn}.',
  'subdomains.record.nameHint': 'The record will be {fqdn}',
  'subdomains.share.add': 'Grant access',
  'subdomains.share.addTitle': 'Share this sub-domain',
  'subdomains.delete.action': 'Delete sub-domain',
  'subdomains.delete.title': 'Delete {fqdn}?',
  'subdomains.delete.keepTitle': 'Delete the sub-domain only, KEEP the DNS records',
  'subdomains.delete.keepSub':
    'Records keep working on Cloudflare; they just stop being managed as a separate branch.',
  'subdomains.delete.purgeTitle': 'Delete the sub-domain AND all {n} DNS records in it',
  'subdomains.delete.purgeSub':
    'Records are really deleted on Cloudflare. Anything using them stops working immediately.',
  'subdomains.delete.purgeWarn':
    'This CANNOT be undone. After deletion, {fqdn} and every name below it disappear from the Internet.',
  'subdomains.delete.confirmLabel': 'Type {fqdn} to confirm',
  'subdomains.delete.grantsNote':
    'Either way, every share and pending invitation on this sub-domain is revoked.',
  'subdomains.delete.pendingWarn':
    '{n} people are still waiting to activate. Their access to this branch is revoked, but the account invitation emails already sent still work — they can still create an account and then sign in to an empty place.',
  'subdomains.delete.done': 'Sub-domain deleted. DNS records were kept.',
  'subdomains.delete.doneWithRecords': 'Deleted the sub-domain and {n} DNS records.',
  'subdomains.share.pending': 'Pending activation',
  'subdomains.share.pendingSub': 'No account yet — invitation sent',
  'subdomains.share.pendingExpires': 'Invitation expires {date}',
  'subdomains.share.revokeInvite': 'Revoke invitation',
  'subdomains.share.inviteRevoked': 'Invitation revoked.',
  'subdomains.share.email': 'Recipient email',
  'subdomains.share.emailHint':
    'They will be able to manage DNS records inside this branch. An email without an account receives an invitation to sign up.',
  'subdomains.share.invited': 'Invitation sent. Access applies once they activate their account.',
  'subdomains.field.shareEmail': 'Share with (optional)',
  'subdomains.field.shareEmailHint':
    'Leave empty to just create the sub-domain. Enter an email to share it right away — if there is no account yet, an invitation is sent.',
  'subdomains.share.granted': 'Access granted on the sub-domain.',
  'subdomains.share.revoke': 'Revoke access',
  'subdomains.share.revoked': 'Access revoked.',
  'subdomains.share.emptyTitle': 'Not shared with anyone',
  'subdomains.share.emptyBody': 'Grant access so someone else can manage records in this branch.',
  'subdomains.share.col.user': 'User',
  'subdomains.share.col.role': 'Role',
  'subdomains.share.col.effective': 'Effective permissions',
  'subdomains.share.effectiveCount': '{n} DNS record permissions',
  'subdomains.share.userPlaceholder': 'Select a user…',
  'subdomains.share.rolePlaceholder': 'Select a role…',
  'subdomains.share.roleHint':
    'Permissions that make no sense at sub-domain level (SSL, plan, nameservers, sharing further) are removed automatically.',
  'subdomains.share.oneLevelNote':
    'People you share with can only manage records inside this branch. They CANNOT create a second-level sub-domain and cannot share it further.',
  'nav.dns': 'DNS records',
  'nav.users': 'Users',
  'nav.roles': 'System roles',
  'nav.domainRoles': 'Domain roles',
  'nav.permissions': 'Domain access',
  'nav.connections': 'Cloudflare connections',
  'nav.settings': 'System settings',
  'nav.profile': 'Profile & security',
  'nav.audit': 'Activity log',
  'nav.open': 'Open navigation',
  'nav.close': 'Close navigation',
  'nav.themeToLight': 'Switch to light theme',
  'nav.themeToDark': 'Switch to dark theme',
  // OQ-288 — ba chế độ giao diện.
  'nav.theme.label': 'Theme',
  'nav.theme.light': 'Light',
  'nav.theme.dark': 'Dark',
  'nav.theme.auto': 'Auto',
  'nav.theme.autoNow.light': 'currently light',
  'nav.theme.autoNow.dark': 'currently dark',
  'nav.logout': 'Sign out',
  'nav.searchPlaceholder': 'Search domains, records, users…',

  // ── Đăng nhập ────────────────────────────────────────────
  'auth.login.title': 'Sign in',
  'auth.login.lede': 'Manage domains and DNS records on Cloudflare.',
  'auth.login.email': 'Email',
  'auth.login.password': 'Password',
  'auth.login.remember': 'Remember me',
  'auth.login.forgot': 'Forgot password?',
  'auth.login.toRegister': 'No account yet? Sign up',
  'auth.register.title': 'Create an account',
  'auth.register.closed':
    'This system is not accepting new sign-ups right now. If you need an account, ask an administrator to invite you.',
  'auth.register.lede': 'Sign up to manage your own Cloudflare domains.',
  'auth.register.submit': 'Sign up',
  'auth.register.haveAccount': 'Already have an account? Sign in',
  'auth.register.sentTitle': 'Check your inbox',
  'auth.register.sentLede': 'If {email} has no account yet, a verification email is on its way.',
  'auth.register.sentHint':
    'Open it and click the link to get started. The link works only once. If this address already has an account, you will get an email reminding you to sign in instead.',
  'auth.verifySignup.title': 'Verifying…',
  'auth.verifySignup.working': 'One moment, we are activating your account.',
  'auth.login.submit': 'Sign in',
  'auth.login.submitting': 'Signing in…',
  'auth.2fa.title': 'Two-factor authentication',
  'auth.2fa.lede': 'Enter the 6-digit code from your authenticator app, or a recovery code.',
  'auth.2fa.code': 'Verification code',
  'auth.2fa.codeHint': '6-digit code or 10-character recovery code',
  'auth.2fa.trust': 'Trust this device for 30 days',
  'auth.2fa.submit': 'Verify',
  'auth.2fa.backToLogin': 'Back to sign in',
  'auth.forgot.title': 'Forgot password',
  'auth.forgot.lede': "Enter your email. If an account exists, we'll send a reset link.",
  'auth.forgot.submit': 'Send reset link',
  'auth.forgot.sent':
    'If this email has an account, a reset link has been sent. The link expires in 60 minutes.',
  'auth.reset.title': 'Reset password',
  'auth.reset.lede': 'Choose a new password for your account.',
  'auth.reset.newPassword': 'New password',
  'auth.reset.confirmPassword': 'Confirm new password',
  'auth.reset.submit': 'Reset password',
  'auth.reset.done': 'Password changed. All previous sessions have been ended.',
  'auth.reset.goLogin': 'Sign in again',
  'auth.invite.title': 'Activate account',
  'auth.invite.lede': '{inviter} has invited you to join as {role}.',
  'auth.invite.fullName': 'Full name',
  'auth.invite.submit': 'Activate and sign in',
  'auth.verifyEmail.title': 'Verify email',
  'auth.verifyEmail.done': 'Verified. Your new email is {email}.',
  'auth.mismatch': 'The two passwords do not match.',
  'auth.mustChangePassword':
    'An administrator just reset your password. Please change it to your own before continuing.',
  'auth.mustSetup2fa':
    'Your role requires two-factor authentication. Set it up before using other screens.',

  // ── Độ mạnh mật khẩu ─────────────────────────────────────
  'password.strength': 'Strength',
  'password.strength.0': 'Very weak',
  'password.strength.1': 'Weak',
  'password.strength.2': 'Fair',
  'password.strength.3': 'Strong',
  'password.strength.4': 'Very strong',
  'password.hint': 'At least 12 characters, including uppercase, lowercase, and a number.',

  // ── Tổng quan ────────────────────────────────────────────
  'overview.title': 'Overview',
  'overview.greeting': 'Hello {name}',
  'overview.ledeAdmin': 'System-wide view: {domains} domains, plus settings and admin logs.',
  'overview.ledeUser': 'Personal view: {domains} domains you added or were granted.',
  'overview.stat.domains': 'Domains',
  'overview.stat.domainsSub': 'Within your scope',
  'overview.stat.records': 'DNS records',
  'overview.stat.recordsSub': 'Across domains you can see',
  'overview.stat.attention': 'Needs attention',
  'overview.stat.attentionSub': 'Pending nameservers or errors',
  'overview.stat.connections': 'Cloudflare connections',
  'overview.stat.connectionsSub': 'Currently healthy',
  'overview.recent.title': 'Recently updated domains',
  'overview.recent.sub': 'Five most recent changes',
  'overview.recent.all': 'View all',
  'overview.alerts.title': 'To do',
  'overview.alerts.sub': 'Ordered by risk',
  'overview.alerts.empty': 'Nothing needs attention',
  'overview.activity.title': 'Recent activity',
  'overview.activity.empty': 'No activity yet',
  'overview.alert.ns_pending.when': 'Watch',
  'overview.alert.ns_pending.title': '{count} domains without nameservers',
  'overview.alert.ns_pending.body': 'The zone exists but receives no traffic yet.',
  'overview.alert.ssl_expiring.when': 'Upcoming',
  'overview.alert.ssl_expiring.title': '{count} SSL certificates expire within 15 days',
  'overview.alert.ssl_expiring.body': 'Review the SSL/TLS settings of the zone.',
  'overview.alert.connection_invalid.when': 'High risk',
  'overview.alert.connection_invalid.title':
    '{count} Cloudflare connections failed to authenticate',
  'overview.alert.connection_invalid.body': 'The token was revoked or lacks scopes.',
  'overview.alert.twofa_missing.when': 'Policy',
  'overview.alert.twofa_missing.title': '{count} accounts without 2FA',
  'overview.alert.twofa_missing.body': 'The system policy requires two-factor authentication.',
  'overview.start.title': 'Get started in three steps',
  'overview.start.sub': 'From adding a token to inviting a collaborator',
  'overview.start.step1': 'Add a Cloudflare connection',
  'overview.start.step1Body': 'Paste your API token; it is verified before being stored.',
  'overview.start.step2': 'Add a domain',
  'overview.start.step2Body': 'The zone is created on Cloudflare, then point the nameservers.',
  'overview.start.step3': 'Invite a collaborator',
  'overview.start.step3Body': 'Share a single domain by email without opening the whole account.',
  'overview.yourRole': 'Your role',
  'overview.capabilities': 'System capabilities',
  'overview.capabilityCount': '{count} system capabilities',
  'overview.security': 'Account security',
  'overview.twofaOn': 'Two-factor authentication enabled',
  'overview.twofaOff': 'Two-factor authentication not enabled',
  'overview.setup2fa': 'Enable now',
  'overview.lastLogin': 'Last sign-in',
  'overview.quickLinks': 'Shortcuts',

  // ── Tìm kiếm toàn cục (FR-005) ───────────────────────────
  'search.empty': 'No results found',
  'search.group.domains': 'Domains',
  'search.group.records': 'DNS records',
  'search.group.users': 'Users',
  'search.usersAdminOnly': 'Only administrators can search users.',

  // ── Hồ sơ ────────────────────────────────────────────────
  'profile.title': 'Profile & security',
  'profile.lede':
    'Your personal information, password, two-factor authentication, and active sessions.',
  'profile.tab.info': 'Details',
  'profile.tab.security': 'Security',
  'profile.tab.sessions': 'Sessions',
  'profile.tab.prefs': 'Notifications',

  'profile.info.heading': 'Profile',
  'profile.info.avatar': 'Avatar',
  'profile.info.avatarHint': 'PNG, JPG or WebP, up to 2 MB.',
  'profile.info.avatarUpload': 'Change avatar',
  'profile.info.fullName': 'Full name',
  'profile.info.email': 'Email',
  'profile.info.emailLogin': 'Sign-in email',
  'profile.info.emailHint':
    'Changing your email requires verification via a link sent to the new address.',
  'profile.info.phone': 'Phone number',
  'profile.info.timezone': 'Timezone',
  'profile.info.locale': 'Language',
  'profile.info.changeEmail': 'Change email',
  'profile.info.newEmail': 'New email',
  'profile.info.currentPasswordToConfirm': 'Confirm with password',
  'profile.info.emailSent':
    'A verification email has been sent to the new address. Your email will only change after you verify.',
  'profile.info.saved': 'Profile saved.',

  'profile.account.heading': 'Account',
  'profile.account.role': 'Role',
  'profile.account.email': 'Email',
  'profile.account.joined': 'Joined',
  'profile.account.domains': 'Managed domains',
  'profile.account.timezone': 'Timezone',

  'profile.score.heading': 'Security score',
  'profile.score.met': '{met} / {total} recommendations met',
  'profile.score.level.ok': 'Good',
  'profile.score.level.warn': 'Fair',
  'profile.score.level.bad': 'Weak',
  'profile.score.todo': 'not met',
  'profile.score.password_fresh': 'Password changed recently',
  'profile.score.twofa_enabled': 'TOTP two-factor authentication',
  'profile.score.recovery_codes': 'Recovery codes available',
  'profile.score.email_verified': 'Email verified',
  'profile.score.passkey_added': 'A passkey is set up',

  'profile.password.heading': 'Change password',
  'profile.password.sub': 'After changing, all other sessions will be ended.',
  'profile.password.current': 'Current password',
  'profile.password.new': 'New password',
  'profile.password.confirm': 'Confirm new password',
  'profile.password.submit': 'Update password',
  'profile.password.signOutOthers': 'Sign out all other devices',
  'profile.password.signOutOthersSub':
    'Always enabled to protect your account after changing password.',
  'profile.password.done': 'Password changed. Other sessions have been signed out.',
  'profile.password.doneTitle': 'Password changed',
  'profile.password.doneMessage':
    'Your password has been changed successfully. Please sign in again with the new password.',
  'profile.password.doneNote':
    'When you press Agree, every session on all devices will be ended and you will be taken back to the sign-in page.',
  'common.agree': 'Agree',

  'profile.2fa.heading': 'Two-factor authentication',
  'profile.2fa.sub': 'A second layer of protection at sign-in',
  'profile.2fa.chipOn': '2FA enabled',
  'profile.2fa.chipOff': '2FA disabled',
  'profile.2fa.totp': 'Authenticator app (TOTP)',
  'profile.2fa.setupOn': 'Set up {date}',
  'profile.2fa.notSet': 'No authenticator app set up.',
  'profile.2fa.enable': 'Set up',
  'profile.2fa.reconfigure': 'Reconfigure',
  'profile.2fa.off': 'Disable',
  'profile.2fa.keep': 'Keep 2FA',
  'profile.2fa.disable': 'Disable two-factor authentication',
  'profile.2fa.setupTitle': 'Set up two-factor authentication',
  'profile.2fa.setupStep1': 'Scan the QR code with Google Authenticator, 1Password, or Authy',
  'profile.2fa.setupStep2': "Can't scan? Enter the key manually",
  'profile.2fa.setupStep3': 'Enter the 6-digit code from your app',
  'profile.2fa.recoveryNote':
    "Once enabled, you'll receive 10 recovery codes. Store them somewhere safe — each code can only be used once.",
  'profile.2fa.confirm': 'Confirm & enable 2FA',
  'profile.2fa.enabled': 'Two-factor authentication enabled.',
  'profile.2fa.disabled': 'Two-factor authentication disabled.',
  'profile.2fa.disableTitle': 'Disable two-factor authentication',
  'profile.2fa.disableWarn': 'Your account will only be protected by your password.',
  'profile.2fa.currentCode': 'Current verification code',

  'profile.recovery.heading': 'Recovery codes',
  'profile.recovery.sub': 'Use when you lose access to your authenticator device',
  'profile.recovery.remaining': '{remaining} / {total} codes remaining',
  'profile.recovery.none': 'No codes yet',
  'profile.recovery.hidden':
    "Codes are shown only once when generated. If you've lost them, generate a new set — the old ones will be invalidated.",
  'profile.recovery.need2fa':
    'Recovery codes are for when you lose access to your authenticator device, so they only apply after enabling two-factor authentication. Set up an authenticator app first.',
  'profile.recovery.regenerate': 'Generate new codes',
  'profile.recovery.saved': 'I have saved these codes somewhere safe',
  'profile.recovery.warn': 'Codes are shown only once. Save them now.',
  'profile.recovery.done': '10 new recovery codes generated. Old codes are no longer valid.',

  'profile.sessions.heading': 'Sign-in sessions',
  'profile.sessions.sub': 'Devices currently signed in to your account.',
  'profile.sessions.device': 'Device',
  'profile.sessions.location': 'Location (approx.)',
  'profile.sessions.ip': 'IP address',
  'profile.sessions.lastSeen': 'Last active',
  'profile.sessions.actions': 'Actions',
  'profile.sessions.other': 'Other session',
  'profile.sessions.locThisMachine': 'This machine',
  'profile.sessions.locLan': 'Local network',
  'profile.sessions.locUnknown': 'Unknown',
  'profile.sessions.ipLoopback': 'this machine',
  'profile.sessions.current': 'Current session',
  'profile.sessions.thisDevice': 'This device',
  'profile.sessions.active': 'Active',
  'profile.sessions.trusted': 'Trusted for 2FA',
  'profile.sessions.end': 'Sign out',
  'profile.sessions.endOthers': 'Sign out all other devices',
  'profile.sessions.ended': 'Session ended.',
  'profile.sessions.endedOthers': 'Ended {count} other sessions.',
  'profile.sessions.expiryNote':
    'Sessions expire automatically after the configured period of inactivity.',

  'profile.securityLog.heading': 'Security log',
  'profile.securityLog.sub': 'The 20 most recent events on your account.',
  'profile.securityLog.action': 'Action',

  'profile.prefs.heading': 'Email notifications',
  'profile.prefs.sub': 'Applies to your account only.',
  'profile.prefs.domain_shared': 'A domain has been shared with me',
  'profile.prefs.domain_shared.sub': 'Sent when someone grants you access to a domain.',
  'profile.prefs.security_alerts': 'Security alerts',
  'profile.prefs.security_alerts.sub': 'Password changes, 2FA changes, suspicious activity.',
  'profile.prefs.new_device': 'Sign-in from a new device',
  'profile.prefs.new_device.sub': 'Cannot be disabled for security reasons.',

  // ── Vai trò hệ thống ─────────────────────────────────────
  'roles.title': 'System roles',
  'roles.lede':
    'Defines the permission types in the system. Each account has a system role; per-domain DNS permissions are defined on the Domain roles screen.',
  'roles.listTitle': 'Roles',
  'roles.count': '{count} roles',
  'roles.add': 'Add role',
  'roles.addNew': 'Add new role',
  'roles.assignedTo': 'Assigned to',
  'roles.userCount': '{count} users',
  'roles.noUser': 'No users yet',
  'roles.systemNote':
    'System roles cannot be edited or deleted; they always retain their full capabilities.',
  'roles.defaultNote': 'The default role is assigned to all new accounts.',
  'roles.footNote':
    'Changes apply to all accounts with this role and are recorded in the activity log.',
  'roles.reset': 'Restore defaults',
  'roles.saveRole': 'Save role',
  'roles.name': 'Role name',
  'roles.description': 'Description',
  'roles.setDefault': 'Set as default role for new users',
  'roles.deleteTitle': 'Delete role',
  'roles.deleteBody': 'Role "{name}" will be permanently deleted.',
  'roles.deleteInUse': 'This role has {count} users. Choose a replacement role for them.',
  'roles.fallbackRole': 'Replacement role',
  'roles.created': 'Role "{name}" created.',
  'roles.updated': 'Role "{name}" saved.',
  'roles.duplicated': 'Duplicated as "{name}".',
  'roles.deleted': 'Role deleted.',
  'roles.wasReset': 'Default capabilities restored.',
  'roles.capabilities': 'Capabilities',
  'roles.matrixTitle': 'Comparison matrix',
  'roles.matrixSub': 'Capability matrix for all current roles.',
  'roles.capabilityCount': '{on}/{total}',

  // ── Mức quyền tên miền ───────────────────────────────────
  'domainRoles.title': 'Domain roles',
  'domainRoles.lede':
    'Defines what a user can do on a specific domain. Access levels are assigned via the Domain access screen.',
  'domainRoles.listTitle': 'Access levels',
  'domainRoles.count': '{count} access levels',
  'domainRoles.add': 'Add domain role',
  'domainRoles.grantCount': '{count} grants',
  'domainRoles.level': 'Level',
  'domainRoles.systemNote':
    'The Owner level is created automatically when a domain is added or ownership is transferred; it cannot be edited or deleted.',
  'domainRoles.affected': 'This change immediately affects {count} active grants using this level.',
  'domainRoles.matrixTitle': 'Comparison matrix',
  'domainRoles.matrixSub': 'Capability matrix for all current access levels.',
  'domainRoles.deleteTitle': 'Delete access level',
  'domainRoles.deleteInUse':
    'This access level has {count} active grants. Choose a replacement level.',
  'domainRoles.fallbackRole': 'Replacement level',
  'domainRoles.created': 'Access level "{name}" created.',
  'domainRoles.updated': 'Access level "{name}" saved.',
  'domainRoles.deleted': 'Access level deleted.',

  // ── Nhật ký hành động (nhãn) ─────────────────────────────
  'action.auth.login': 'Login',
  'action.auth.login_failed': 'Login failed',
  'action.auth.logout': 'Logout',
  'action.auth.new_device': 'New device login',
  'action.auth.password_reset': 'Password reset',
  'action.auth.2fa_enabled': '2FA enabled',
  'action.auth.2fa_disabled': '2FA disabled',
  'action.auth.recovery_used': 'Recovery code used',
  'action.auth.passkey_added': 'Passkey added',
  'action.auth.passkey_removed': 'Passkey removed',
  'action.auth.passkey_login': 'Signed in with passkey',
  'action.user.update': 'Account updated',
  'action.user.role_change': 'Role changed',
  'action.user.suspend': 'Account suspended',
  'action.user.force_2fa': '2FA enforcement enabled',
  'action.user.sessions_revoked': 'Sessions revoked',
  'action.grant.create': 'Domain access granted',
  'action.grant.update': 'Access level changed',
  'action.grant.revoke': 'Access revoked',
  'action.grant.invite': 'Domain access invitation sent',
  'action.backup.remote_add': 'Backup storage added',
  'action.backup.remote_update': 'Backup storage updated',
  'action.backup.remote_connect': 'Backup storage connected',
  'action.backup.remote_check': 'Backup storage tested',
  'action.backup.remote_remove': 'Backup storage removed',

  /* OQ-337 — Cloudflare Tunnel. */
  'action.tunnel.create': 'Tunnel created',
  'action.tunnel.delete': 'Tunnel deleted',
  'action.tunnel.token_view': 'Tunnel install token viewed',
  'action.tunnel.config_update': 'Tunnel configuration updated',
  'action.subdomain.create': 'Sub-domain created',
  'action.subdomain.delete': 'Sub-domain deleted',
  'action.subdomain.grant': 'Sub-domain shared',
  'action.subdomain.grant_update': 'Sub-domain role changed',
  'action.subdomain.revoke': 'Sub-domain access revoked',
  'action.user.invite': 'User invited',
  'action.user.create': 'Account created',
  'action.user.delete': 'Account deleted',
  'action.domain.create': 'Domain added',
  'action.domain.update': 'Domain updated',
  'action.domain.delete': 'Domain deleted',
  'action.domain.sync': 'Domain synced',
  'action.domain.transfer': 'Ownership transferred',
  'action.domain.pause': 'Domain paused',
  'action.domain.purge_cache': 'Cache purged',
  'action.domain.ns_verify': 'Nameserver verified',
  'action.dns.create': 'DNS record added',
  'action.dns.update': 'DNS record updated',
  'action.dns.delete': 'DNS record deleted',
  'action.dns.proxy_toggle': 'Cloudflare proxy toggled',
  'action.dns.import': 'Zone file imported',
  'action.dns.export': 'Zone file exported',
  'action.dns.sync_removed': 'Record removed during sync',
  'action.role.create': 'System role created',
  'action.role.update': 'System role updated',
  'action.role.delete': 'System role deleted',
  'action.role.reset': 'System role reset to default',
  'action.domainrole.create': 'Domain role created',
  'action.domainrole.update': 'Domain role updated',
  'action.domainrole.delete': 'Domain role deleted',
  'action.connection.create': 'Cloudflare connection added',
  'action.connection.update': 'Cloudflare connection updated',
  'action.connection.verify': 'Cloudflare token verified',
  'action.connection.rotate': 'Cloudflare token rotated',
  'action.connection.set_default': 'Default connection set',
  'action.connection.pause': 'Connection paused',
  'action.connection.delete': 'Cloudflare connection deleted',
  'action.setting.update': 'System settings updated',
  'action.setting.smtp_test': 'SMTP test email sent',
  'result.success': 'Success',
  'result.failure': 'Failed',
  // ── Phân quyền tên miền — permissions.html (FR-070…085) ──
  'perm.title': 'Domain permissions',
  'perm.ledeAdmin':
    'By default, each domain is visible only to the person who added it. Browse by user or by domain, then grant or revoke access right there.',
  'perm.ledeUser':
    'Share the domains you added with colleagues. Select a domain to see who has access, then grant or revoke it.',
  'perm.manageDomainRoles': 'Manage domain roles',
  'perm.userScopeNote':
    'You can only manage permissions for domains you added yourself. Domains shared with you by others do not appear here.',
  'perm.tab.byUser': 'By user',
  'perm.tab.byDomain': 'By domain',

  'perm.users': 'Users',
  'perm.domains': 'Domains',
  'perm.filterUsers': 'Filter users…',
  'perm.filterDomains': 'Filter domains…',
  'perm.noUserMatch': 'No users match the filter.',
  'perm.noDomainMatch': 'No domains match the filter.',
  'perm.noDomainOwned': 'You have not added any domains to share.',
  'perm.domainsSeen': 'domains visible',
  'perm.membersCount': 'users with access',
  'perm.recordsCount': '{count} records',
  'perm.ownedBy': 'Owned by {name}',
  'perm.addedByYou': 'Added by you',

  'perm.col.domain': 'Domain',
  'perm.col.user': 'User',
  'perm.col.relation': 'Relation',
  'perm.col.level': 'Access level',
  'perm.col.grantedAt': 'Granted at',
  'perm.col.actions': 'Actions',
  'perm.relation.owned': 'Added it',
  'perm.relation.granted': 'Granted',
  'perm.relation.viewall': 'System-wide access',
  'perm.ownerLevel': 'Owner',
  'perm.notRevocable': 'Not revocable',
  'perm.grantedWhenAdded': 'When domain was added',
  'perm.grantedBy': 'by {name}',
  'perm.revoke': 'Revoke',
  'perm.addDomain': 'Add domain',
  'perm.addUser': 'Add user',

  'perm.userFootnote':
    'Only domains in this table are visible when the user signs in. Changes are recorded in the activity log.',
  'perm.emptyUserTitle': 'This user has no domains',
  'perm.emptyUserBody':
    'They have not added any domains themselves and have not been granted access to any. Use "Add domain" to assign their first domain.',
  'perm.emptyUserAction': 'Add domain for this user',
  'perm.emptyDomainTitle': 'You have not added any domains',
  'perm.emptyDomainBody': 'Only domains you added yourself appear here to share with others.',
  'perm.emptyNoUsers': 'No users yet',

  // Hộp thoại "Thêm tên miền cho người dùng" (FR-073)
  'perm.addDomainTitle': 'Add domain for user',
  'perm.addDomainSub': 'Grant access to {name}. The domain will appear in their list immediately.',
  'perm.addDomainField': 'Unassigned domains',
  'perm.addDomainHint': '{count} domains not yet visible to this user.',
  'perm.addDomainNone': 'This user already has access to all domains.',
  'perm.addDomainNoneOption': 'No domains left to grant',
  'perm.grant': 'Grant access',

  // Luồng thêm người dùng bằng email (FR-080…085)
  'perm.addUserTitle': 'Add user to domain',
  'perm.addUserSub': 'Grant access to {name}. Enter an email to find an account.',
  'perm.shareTitle': 'Grant access to {name}',
  'perm.shareSub':
    'Enter the email of the person to add. If they have no account, we will send an invitation.',
  'perm.email': 'User email',
  'perm.emailPlaceholder': 'name@company.com',
  'perm.lookup': 'Add',
  'perm.lookupHint': 'The system will look up a matching account before granting access.',
  'perm.lookupInvalid': 'Enter a valid email address to look up an account.',
  'perm.lookupFound': 'Account found.',
  'perm.lookupMissing': 'No account found — send an invitation instead.',
  'perm.lookupGranted':
    '{name} already has access to this domain. Change their access level instead.',
  'perm.lookupTooMany': 'Too many lookups. Try again in a minute.',
  'perm.changeEmail': 'Change email',
  'perm.notify': 'Send notification email',
  'perm.notifySub': 'Notify the user that they have been granted access.',
  'perm.inviteWarn':
    'No account exists for {email}. Send an invitation to create one — the access level below will apply automatically when they activate it.',
  'perm.inviteName': 'Display name (optional)',
  'perm.inviteNamePlaceholder': 'e.g. Jane Smith',
  'perm.inviteLevel': 'Access level granted upon activation',
  'perm.inviteMessage': 'Message in invitation email',
  'perm.inviteMessageDefault': "Hi, I'd like to invite you to help manage DNS on DNSHub.",
  'perm.inviteExpiry': 'Invitation expiry',
  'perm.inviteExpirySub': 'The activation link expires after {days} days.',
  'perm.inviteDays': '{days} days',
  'perm.sendInvite': 'Send invitation',
  'perm.pendingTitle': 'Pending invitation',
  'perm.pendingChip': 'Awaiting activation',
  'perm.pendingResend': 'Resend',
  'perm.inviteResent': 'Invitation resent to {email}.',
  'perm.pendingRevoke': 'Cancel invitation',

  // Chuyển quyền sở hữu

  // Thông báo
  'perm.granted': 'Granted access to {domain} for {name}.',
  'perm.levelChanged': '{name} → {domain}: {level}.',
  'perm.revoked': "Revoked {name}'s access to {domain}.",
  'perm.invited': 'Account invitation sent to {email}.',
  'perm.inviteRevoked': 'Invitation to {email} cancelled.',

  // ── Quyền truy cập trong chi tiết tên miền (dùng lại ở M4) ──
  'perm.accessGrantButton': 'Grant user access',
  'perm.accessSource': 'Source',
  'perm.sourceOwner': 'Domain owner',
  'perm.sourceManual': 'Manually granted',
  'perm.sourceViewall': 'System admin access',

  // ── Thông báo trong ứng dụng (M6) ────────────────────────
  'nav.notifications': 'Notifications',
  'notifications.empty': 'No notifications yet.',
  'notifications.markAll': 'Mark all read',

  // ── Người dùng · FR-050…056 ──────────────────────────────
  'users.title': 'Users',
  'users.lede':
    'Create accounts, assign system roles, and monitor security status. Per-domain permissions are configured under Domain access.',
  'users.export': 'Export CSV',
  'users.invite': 'Invite user',
  'users.create': 'Create account',

  'users.createModal.title': 'Create new account',
  'users.createModal.subtitle': 'Account works immediately — no invitation email needed.',
  'users.createModal.email': 'Email',
  'users.createModal.fullName': 'Full name',
  'users.createModal.role': 'System role',
  'users.createModal.submit': 'Create account',
  'users.createModal.doneTitle': 'Account created',
  'users.createModal.note':
    'The account is activated immediately and the email is treated as verified — you are vouching for the address in place of the email verification round-trip.',

  'users.resetModal.title': 'Reset password',
  'users.resetModal.submit': 'Set new password',
  'users.resetModal.doneTitle': 'Password reset',
  'users.resetModal.note':
    'All of the user\\u2019s open sessions will be revoked. They must sign in again with the new password.',

  'users.password.label': 'New password',
  'users.password.placeholder': 'Leave empty — the system will generate one',
  'users.password.generate': 'Generate',
  'users.password.show': 'Show password',
  'users.password.hide': 'Hide password',
  'users.password.hint': 'Click the field to type your own, or leave it empty to auto-generate.',
  'users.password.mustChange': 'Require the user to change password at next sign-in',
  'users.password.mustChangeNote':
    'The user will be taken to the change-password screen right after signing in.',
  'users.password.success': 'Password for {email} changed successfully.',
  'users.password.createdSuccess': 'Account {email} created successfully.',
  'users.password.newPassword': 'New password',
  'users.password.onceOnly': 'Shown only once. Copy it before closing this dialog.',
  'users.password.copied': 'Copied',
  'users.password.copyFailed': 'Copy failed — select the text and copy manually.',
  'users.password.linkSent':
    'A password-reset link was also emailed to the user. It expires in {hours} hours, or as soon as they sign in with the password above.',
  'users.password.linkFailed':
    'Email could not be sent — the password was changed successfully, please pass it to the user yourself.',

  'users.detail.resetPassword': 'Reset password',
  'users.stat.active': 'Active accounts',
  'users.stat.activeSub': 'Out of {total} total',
  'users.stat.twofa': '2FA enabled',
  'users.stat.twofaSub': '{percent}% — target 100%',
  'users.stat.invited': 'Pending invitations',
  'users.stat.invitedSub': 'No expired invitations',
  'users.stat.invitedExpired': 'expired invitations',
  'users.stat.suspended': 'Suspended',
  'users.stat.suspendedSub': 'Disabled by an administrator',
  'users.stat.locked': 'Locked',
  'users.stat.lockedSub': 'Due to too many failed sign-in attempts',
  'users.filter.all': 'All',
  'users.filter.admin': 'Admins',
  'users.filter.member': 'Members',
  'users.filter.invited': 'Pending',
  'users.filter.suspended': 'Suspended',
  'users.filter.locked': 'Locked',
  'users.searchPlaceholder': 'Search by name or email…',
  'users.searchAria': 'Search users',
  'users.selectAll': 'Select all',
  'users.selectOne': 'Select {name}',
  'users.col.user': 'User',
  'users.col.name': 'Full name',
  'users.col.role': 'System role',
  'users.col.domains': 'Domains',
  'users.col.twofa': '2FA',
  'users.col.lastLogin': 'Last sign-in',
  'users.col.status': 'Status',
  'users.col.actions': 'Actions',
  'users.status.active': 'Active',
  'users.status.invited': 'Pending',
  'users.status.suspended': 'Suspended',
  'users.status.locked': 'Locked',
  'users.twofa.required': 'Required',
  'users.twofa.off': 'Not enabled',
  'users.twofa.none': 'Not set up',
  'users.neverLogin': 'Never signed in',
  'users.action.self': 'You',
  'users.action.resend': 'Resend',
  'users.action.revoke': 'Revoke',
  'users.action.unlock': 'Unlock',
  'users.action.permissions': 'Permissions',
  'users.action.detail': 'Details',
  'users.action.remind2fa': 'Remind 2FA',
  'users.foot.selected': '{count} users selected',
  'users.bulk.force2fa': 'Require 2FA',
  'users.bulk.suspend': 'Suspend',
  'users.bulk.activate': 'Reactivate',
  'users.bulk.activateHint': 'Reopen {count} suspended or locked account(s).',
  'users.bulk.suspendHint': 'Suspend {count} active account(s).',
  'users.empty.title': 'No users yet',
  'users.empty.body': 'Invite the first person to the system.',
  'users.empty.filter': 'No users match the filter',
  'users.empty.filterBody': 'Try adjusting the filters or changing the search term.',
  'users.toast.exported': 'User list exported (CSV).',
  'users.toast.invited': 'Invitation sent to {email}.',
  'users.toast.resent': 'Invitation resent to {email}.',
  'users.toast.force2fa': '2FA required at next sign-in.',
  'users.toast.unlocked': 'Account unlocked.',
  'users.toast.suspendedMany': 'Suspended {count} account(s).',
  'users.toast.activatedMany': 'Reactivated {count} account(s).',
  'users.toast.deleted': 'Account deleted.',
  'users.toast.saved': 'Changes saved.',
  'users.toast.resetSent': 'Password reset link sent.',
  'users.toast.sessionsRevoked': 'All user sessions ended.',
  'users.toast.twofaDisabled': '2FA disabled — the user must set it up again.',
  'users.inviteModal.title': 'Invite user',
  'users.inviteModal.subtitle':
    'The account is created in pending state until the user sets a password.',
  'users.inviteModal.email': 'Email',
  'users.inviteModal.role': 'System role',
  'users.inviteModal.roleHint': 'Administrators have full access to all domains.',
  'users.inviteModal.expires': 'Invitation expiry (days)',
  'users.inviteModal.message': 'Message',
  'users.inviteModal.submit': 'Send invitation',
  'users.inviteModal.note':
    'Per-domain permissions are granted separately on the Domain access screen and are not included in the invitation.',
  'users.detail.title': 'User details',
  'users.detail.joined': 'joined {date}',
  'users.detail.security': 'Security',
  'users.detail.lastLogin': 'Last sign-in',
  'users.detail.sessions': 'Active sessions',
  'users.detail.sessionCount': '{count} devices',
  'users.detail.passwordChanged': 'Password changed',
  'users.detail.scope': 'Domain scope',
  'users.detail.noDomain': 'No owned domains.',
  'users.detail.openPermissions': 'Open permissions panel',
  'users.detail.profile': 'Profile & role',
  'users.detail.fullName': 'Full name',
  'users.detail.phone': 'Phone number',
  'users.detail.role': 'System role',
  'users.detail.selfRoleNote': 'You cannot change your own role.',
  'users.detail.adminActions': 'Admin actions',
  'users.detail.sendReset': 'Send password reset link',
  'users.detail.endSessions': 'End all sessions',
  'users.detail.reset2fa': 'Reset 2FA',
  'users.detail.force2fa': 'Require 2FA',
  'users.detail.suspend': 'Suspend account',
  'users.detail.activate': 'Reactivate account',
  'users.detail.delete': 'Delete account',
  'users.detail.foot': 'All actions are recorded in the activity log.',
  'users.deleteModal.title': 'Delete user',
  'users.deleteModal.subtitle': 'Account {email} will no longer be able to sign in.',
  'users.deleteModal.ownsDomains':
    'This account owns {count} domain(s). Choose a transfer recipient.',
  'users.deleteModal.transferTo': 'Transfer domains to',
  'users.deleteModal.confirmLabel': 'Type the email to confirm:',
  'users.deleteModal.submit': 'Delete user',

  // ── Cài đặt hệ thống · FR-110…116 ────────────────────────
  'settings.title': 'System settings',
  'settings.lede':
    'Security policy, domain rules, system mail, and branding apply to the whole organization. Cloudflare tokens are at',
  'settings.cloudflareLink': 'Cloudflare connections',
  'settings.restoreDefaults': 'Restore defaults',
  /* OQ-330 — passkey */
  'auth.login.or': 'or',
  'auth.login.passkey': 'Sign in with a passkey',
  'profile.passkey.title': 'Passkeys',
  'profile.passkey.sub':
    'Sign in with your fingerprint, face or device PIN — no password to remember and no OTP to type.',
  'profile.passkey.empty': 'You have no passkeys yet.',
  'profile.passkey.add': 'Add a passkey on this device',
  'profile.passkey.added': 'Passkey added. You can sign in with it next time.',
  'profile.passkey.removed': 'Passkey removed.',
  'profile.passkey.rename': 'Rename',
  'profile.passkey.remove': 'Remove',
  'profile.passkey.neverUsed': 'Never used',
  'profile.passkey.col.name': 'Name',
  'profile.passkey.col.created': 'Created',
  'profile.passkey.col.lastUsed': 'Last used',
  'profile.passkey.unsupported':
    'This browser does not support passkeys. Use a recent Chrome, Edge, Safari or Firefox.',
  'settings.tab.security': 'Security',
  'settings.tab.sync': 'Sync',
  'settings.tab.policy': 'Domain policy',
  'settings.tab.smtp': 'SMTP',
  'settings.tab.mail': 'Email templates',
  'settings.tab.branding': 'Branding',
  'settings.remote.title': 'Off-server backup storage',
  'settings.remote.lede':
    'Backups kept on the server are lost with the server. Connect one or more storage accounts to keep a copy elsewhere.',
  'settings.remote.provider': 'Provider',
  'settings.remote.add': 'Add storage',
  'settings.remote.added': 'Storage added. Click Connect to authorise it.',
  'settings.remote.connect': 'Connect',
  'settings.remote.connected': 'Backup storage connected.',
  'settings.remote.notConnected': 'Not authorised',
  'settings.remote.test': 'Test connection',
  'settings.remote.testOk': 'Connection is fine · {n} backups in storage',
  'settings.remote.remove': 'Remove connection',
  'settings.remote.removeTitle': 'Remove this backup connection?',
  'settings.remote.removeBody':
    'Disconnect {label}. The system will stop uploading backups to this storage.',
  'settings.remote.removeKeepsFiles':
    'Backups already uploaded stay in your account — removing the connection does not delete them.',
  'settings.remote.removed': 'Connection removed.',
  'settings.remote.enabled': 'Enabled',
  'settings.remote.disabled': 'Disabled',
  'settings.remote.enabledHint':
    'This remote IS receiving backups: every new backup is uploaded here. Clear the box to pause it while keeping the connection.',
  'settings.remote.disabledHint':
    'This remote is OFF: the connection is kept but no new backup is uploaded. Tick the box to resume.',
  'settings.remote.pause': 'Pause uploads to this storage',
  'settings.remote.resume': 'Resume this storage',
  'settings.remote.status.ok': 'Healthy',
  'settings.remote.status.invalid': 'Failing',
  'settings.remote.status.unchecked': 'Not tested',
  'settings.remote.emptyTitle': 'No storage connected yet',
  'settings.remote.emptyBody':
    'Pick a provider and click Add storage. You can add several accounts for cross-redundancy.',
  'settings.remote.restoreFrom': 'Restore from cloud',
  'settings.remote.restoreTitle': 'Restore from {label}',
  'settings.remote.restored': 'Restored from off-server storage.',
  'settings.remote.restoreWarn':
    'Restoring overwrites current data. The system creates a safety backup first.',
  'settings.remote.noFilesTitle': 'No backups in this storage yet',
  'settings.remote.noFilesBody': 'Backups will appear here after the next run.',
  'setting.backup.oauth.dropbox.client_id': 'Dropbox · App key',
  'setting.backup.oauth.dropbox.client_secret': 'Dropbox · App secret',
  'setting.backup.oauth.gdrive.client_id': 'Google · Client ID',
  'setting.backup.oauth.gdrive.client_secret': 'Google · Client secret',
  'settings.oauthApp.show': 'No App key / Client ID yet? See the one-time setup guide',
  'settings.oauthApp.hide': 'Hide the app registration guide',
  'settings.oauthApp.title': 'Register an OAuth app',
  'settings.oauthApp.once':
    'Do this ONCE for the whole system. After that, adding any number of accounts is just Connect and sign in.',
  'settings.oauthApp.redirectUri': 'Paste this exact address into the provider Redirect URI field:',
  'settings.oauthApp.thenConnect':
    'Once done, come back here, paste the App key / Client ID below, save changes, then click Connect on each storage.',
  'settings.oauthApp.dropbox1':
    'Open the Dropbox App Console → **Create app** → pick *Scoped access* → *App folder* → name it.',
  'settings.oauthApp.dropbox2':
    'On the **Permissions** tab: tick files.content.write, files.content.read and account_info.read → **Submit**.',
  'settings.oauthApp.dropbox3':
    'On the **Settings** tab, under OAuth 2 → Redirect URIs: paste the address above and click **Add**.',
  'settings.oauthApp.dropbox4': 'Still on Settings: copy the **App key** and **App secret**.',
  'settings.oauthApp.gdrive1':
    'Open the Google Cloud Console → create a project → enable the **Google Drive API** under APIs & Services → Library.',
  'settings.oauthApp.gdrive2':
    'Go to the OAuth consent screen: choose **External**, fill in the name and email, and add yourself to **Test users**.',
  'settings.oauthApp.gdrive3':
    'Go to Credentials → **Create credentials → OAuth client ID** → type **Web application**, and paste the address above into **Authorized redirect URIs**.',
  'settings.oauthApp.gdrive4': 'Copy the **Client ID** and **Client secret** that appear.',
  /* OQ-327 — email templates tab */
  'settings.mail.lede':
    'Edit the subject and body for each flow. The system wraps your text with the logo, colours and footer.',
  'settings.mail.listTitle': 'Email flows',
  'settings.mail.listSub': '{count} flows',
  'settings.mail.col.flow': 'Flow',
  'settings.mail.col.state': 'State',
  'settings.mail.state.custom': 'Customised',
  'settings.mail.state.default': 'Default',
  'settings.mail.pickTitle': 'Pick a flow',
  'settings.mail.pickSub': 'Choose a flow from the list on the left.',
  'settings.mail.pickHint': 'No flow selected yet.',
  'settings.mail.subject': 'Subject',
  'settings.mail.body': 'Body',
  'settings.mail.bodyHint':
    'Write plain text. Leave a blank line to start a new paragraph. HTML is not allowed and will show as-is.',
  'settings.mail.vars': 'Available variables',
  'settings.mail.varsHint':
    'Type the name in braces and it will be replaced with the real value. Unknown variables are rejected on save.',
  'settings.mail.noVars': 'This flow has no variables.',
  'settings.mail.varTip': '{moTa} — example: {viDu}',
  'settings.mail.save': 'Save this template',
  'settings.mail.preview': 'Preview',
  'settings.mail.previewTitle': 'This is how the email will look',
  'settings.mail.restore': 'Restore default',
  'settings.mail.saveNote':
    'The "Save changes" button at the top of the page does not save email templates — each template saves on its own.',
  'settings.mail.saved': 'Template saved. Emails sent from now on will use the new content.',
  'settings.mail.restored': 'Template restored to its default content.',
  /* OQ-333 — archive old audit logs */
  'settings.auditArchive.title': 'Archive old audit logs',
  'settings.auditArchive.sub':
    'Write old log entries to a file, then remove them from the database.',
  'settings.auditArchive.lede':
    'Audit logs are append-only and never expire on their own. As the table grows you can archive the older part — the system writes the file, reads it back to verify the line count, and only then deletes. If the file comes out short, nothing is deleted.',
  'settings.auditArchive.before': 'Archive entries older than',
  'settings.auditArchive.beforeHint': 'Logs must be kept for at least 365 days.',
  'settings.auditArchive.count': 'Will archive and delete: {soDong} entries.',
  'settings.auditArchive.run': 'Archive and delete',
  'settings.auditArchive.confirmTitle': 'Delete old audit logs from the database?',
  'settings.auditArchive.confirmBody':
    '{soDong} entries older than {ngay} will be written to a file and removed from the database. The file stays on the server and can be downloaded, but the entries will no longer be searchable in the Audit log page.',
  'settings.auditArchive.done':
    'Archived {soDong} entries to {name} and removed them from the database.',
  'settings.auditArchive.fileRemoved': 'Archive file deleted.',
  'settings.auditArchive.noIndex':
    'The audit search index is not enabled on this database (pg_trgm missing). Search still returns correct results, but will slow down as the table grows.',
  'settings.auditArchive.download': 'Download',
  'settings.auditArchive.col.file': 'File',
  'settings.auditArchive.col.lines': 'Entries',
  'settings.auditArchive.col.size': 'Size',
  'settings.tab.backup': 'Backup',
  'settings.backup.scope':
    'This backs up the DNSHub database — NOT Cloudflare. Real DNS records live on Cloudflare; restoring here does not push anything back to it.',
  'settings.backup.create': 'Back up now',
  'settings.backup.upload': 'Restore from file…',
  'settings.backup.download': 'Download',
  'settings.backup.restore': 'Restore',
  'settings.backup.empty': 'No backups yet.',
  'settings.backup.col.name': 'File name',
  'settings.backup.col.size': 'Size',
  'settings.backup.col.created': 'Created',
  'settings.backup.col.check': 'Check',
  'settings.remote.fileDeleted': 'Backup deleted from the remote.',
  'settings.remote.deleteFileTitle': 'Delete this backup from the remote?',
  'settings.remote.deleteFileBody':
    'Permanently deletes {name} from {label}. The remote has no recycle bin, and no backup run will recreate this file.',
  'settings.backup.col.reason': 'Reason',
  'settings.backup.reason.auto': 'Scheduled',
  'settings.backup.reason.manual': 'Manual',
  'settings.backup.reason.safety': 'Safety copy before restore',
  'settings.backup.reason.unknown': 'Unknown',
  'settings.backup.col.actions': 'Actions',
  // OQ-147 — ba kết quả, không phải hai. Chữ phải tự phân biệt được khi bỏ màu.
  'settings.backup.check': 'Check',
  'settings.backup.check.running': 'Checking…',
  'settings.backup.check.unknown': 'Not checked',
  'settings.backup.check.pass': 'OK',
  'settings.backup.check.empty': 'OK but empty',
  'settings.backup.check.broken': 'Broken',
  'settings.backup.check.brokenUnknown': 'Cannot be opened; server gave no reason.',
  'settings.backup.count.domains': 'domains',
  'settings.backup.count.users': 'users',
  'settings.backup.count.cf_connections': 'connections',
  'settings.backup.count.dns_records': 'records',
  'settings.backup.createdToast': 'New backup created.',
  'settings.backup.deletedToast': 'Deleted {name}.',
  'settings.backup.verifyOkToast': '{name} reads fine, data is intact.',
  'settings.backup.verifyBadToast': '{name} can NOT be used to restore.',
  'settings.backup.restoredToast': 'Restore finished.',
  'settings.backup.restored': 'Restored {rows} rows across {tables} tables.',
  'settings.backup.safety': 'Safety snapshot taken before overwrite: {name}',
  'settings.backup.confirm.title': 'Restore database',
  'settings.backup.confirm.warn':
    'This OVERWRITES ALL current data. A safety snapshot is taken automatically before overwriting.',
  'settings.backup.confirm.hint': 'Type the backup name to confirm: {name}',
  'settings.security.auth': 'Authentication',
  'settings.security.access': 'Access control',
  'settings.security.password': 'Password policy',
  'settings.sync.title': 'Automatic sync',
  'settings.sync.sub': 'Applies to all DNS zones in the system',
  'settings.sync.note': 'Sync jobs run in the background and do not block your actions.',
  'settings.sync.runNow': 'Sync all now',
  'settings.sync.queued': 'Full sync job queued.',

  // ── Theo dõi job nền (docs/09 §9.4) ──────────────────────
  'settings.jobs.title': 'Background job status',
  'settings.jobs.sub': 'Four Cloudflare sync jobs, refreshed every 30 seconds',
  'settings.jobs.never': 'Never run',
  // OQ-289 — 'chưa chạy' và 'không có việc' là hai chuyện khác nhau.
  'settings.jobs.idle': 'Idle',
  'settings.jobs.queued': '{count} jobs pending',
  'setting.sync.verify_ns_interval_minutes': 'Nameserver check interval (minutes)',
  'setting.sync.verify_ns_interval_minutes.hint':
    'Rechecks nameservers for domains pending activation.',
  'setting.sync.dns_sync_interval_minutes': 'Record catch-up sync interval (minutes)',
  'setting.sync.dns_sync_interval_minutes.hint':
    'Scan rate for the catch-up queue. Jobs are queued only when a DNS record write fails — if the queue is empty, nothing runs.',
  'settings.jobs.every': 'every {minutes} minutes',
  'settings.jobs.failures': '{count} failures in 24 hours',
  'settings.jobs.empty': 'No runs recorded yet.',
  'settings.jobs.colKind': 'Job',
  'settings.jobs.colTarget': 'Target',
  'settings.jobs.colStatus': 'Status',
  'settings.jobs.colAttempts': 'Attempts',
  'settings.jobs.colCreated': 'Queued at',
  'settings.jobs.kind.zone_sync': 'Zone & record sync',
  'settings.jobs.kind.token_check': 'Token check',
  'settings.jobs.kind.dns_sync': 'Record catch-up sync',
  'settings.jobs.kind.verify_ns': 'Nameserver verification',
  'settings.jobs.kind.backup': 'Database backup',
  'settings.jobs.status.queued': 'Pending',
  'settings.jobs.status.running': 'Running',
  'settings.jobs.status.done': 'Done',
  'settings.jobs.status.failed': 'Failed',
  'settings.policy.title': 'Domain addition rules',
  'settings.policy.sub': 'Applies to the Member role',
  // FR-112 mục 1 "ai được thêm tên miền" — bảng chỉ đọc, xem OQ-037 / OQ-140.
  'settings.policy.domAdd.title': 'Who can add domains',
  'settings.policy.domAdd.sub': 'Roles with the "Add new domain" capability (dom.add). Read-only.',
  'settings.policy.domAdd.manage': 'Open Roles page',
  'settings.policy.domAdd.colRole': 'Role',
  'settings.policy.domAdd.colUsers': 'Assigned to',
  'settings.policy.domAdd.system': 'System',
  'settings.policy.domAdd.default': 'Default',
  'settings.policy.domAdd.noneTitle': 'No roles can add domains',
  'settings.policy.domAdd.noneBody':
    'No roles currently have the "Add new domain" capability. Only administrators can add domains.',
  'settings.policy.domAdd.needRolesTitle': 'Requires "Manage roles" permission to view',
  'settings.policy.domAdd.needRolesBody':
    'The role list requires the adm.roles capability. Your account only has adm.settings, so this section is empty.',
  'settings.policy.domAdd.foot':
    'No controls here: the ability to add domains is changed on the Roles page, and all changes are logged.',
  'settings.smtp.title': 'Mail server (SMTP)',
  'settings.smtp.sender': 'Sender',
  'settings.smtp.test': 'Send test email',
  'settings.smtp.testTitle': 'Send test email',
  'settings.smtp.testSubtitle': 'Uses the currently saved SMTP configuration.',
  'settings.smtp.testTo': 'Send to',
  'settings.smtp.sent': 'Test email sent to {email}.',
  'settings.smtp.eventsNote':
    'Mail event types are fixed per docs/07 §7.9; users manage their own preferences in their profile.',
  'settings.branding.org': 'Organization info',
  'settings.branding.appearance': 'Default appearance',
  'settings.branding.upload': 'Upload image',
  'settings.branding.uploaded': 'Brand image updated.',
  'settings.branding.noImage': 'No image',
  'settings.saved': 'Saved {count} changes.',
  'settings.changedCount': '{count} unsaved changes',
  'settings.auditNote': 'All settings changes are logged with before → after values.',
  'settings.reset.title': 'Restore defaults',
  'settings.reset.subtitle': 'Applies to the {tab} tab.',
  'settings.reset.body':
    'All keys in this tab will revert to system defaults. SMTP passwords are preserved.',
  'settings.reset.submit': 'Restore',

  // ── Nhãn từng khoá cài đặt ───────────────────────────────
  'setting.security.force_2fa_all': 'Require 2FA for all accounts',
  'setting.security.force_2fa_all.hint':
    'Users without 2FA will be prompted to set it up at their next sign-in.',
  'setting.security.min_password_length': 'Minimum password length',
  'setting.security.min_password_length.hint': 'Between 8 and 64 characters.',
  'setting.security.require_uppercase': 'Require uppercase letter',
  'setting.security.require_lowercase': 'Require lowercase letter',
  'setting.security.require_number': 'Require number',
  'setting.security.require_symbol': 'Require special character',
  'setting.security.require_symbol.hint': 'Recommended but not required by default.',
  'setting.security.password_history': 'Password history limit',
  'setting.security.password_history.hint': 'Set to 0 to disable.',
  'setting.security.password_expiry_days': 'Password expiry (days)',
  'setting.security.password_expiry_days.hint': 'Set to 0 to never expire.',
  'setting.security.check_pwned_passwords': 'Check against breached password list',
  'setting.security.check_pwned_passwords.hint':
    'Uses k-anonymity against api.pwnedpasswords.com; disable when the server has no internet access.',
  'setting.security.max_failed_logins': 'Failed attempts before lockout',
  'setting.security.max_failed_logins.hint': 'Exceeding this threshold locks the account.',
  'setting.security.lockout_minutes': 'Lockout duration (minutes)',
  'setting.security.lockout_minutes.hint': 'After this period, the user can sign in again.',
  'setting.security.session_absolute_days': 'Absolute session timeout (days)',
  'setting.security.session_absolute_days.hint':
    'When expired, the user must sign in again even if active.',
  'setting.security.session_idle_days': 'Idle session timeout (days)',
  'setting.security.session_idle_days.hint': 'Measured from the last activity in the session.',
  'setting.security.allow_multiple_sessions': 'Allow concurrent sessions',
  'setting.security.allow_multiple_sessions.hint':
    'Disable to restrict each account to one device at a time.',
  'setting.security.force_2fa_roles': 'Roles required to use 2FA',
  'setting.security.force_2fa_roles.hint':
    'One role slug per line, e.g. admin. Members of those roles must set up 2FA immediately after sign-in. Leave blank if the toggle above is already on.',
  'setting.security.admin_ip_allowlist': 'Admin access CIDR allowlist',
  'setting.security.admin_ip_allowlist.hint':
    'One CIDR range per line, e.g. 203.0.113.0/24. ACTIVE: empty means no restriction; once any range is set, all IPs outside it are blocked from Users · Roles · Capabilities · Settings. Add your own IP before saving.',
  'setting.security.invitation_expiry_days': 'Default invite expiry (days)',
  'setting.security.invitation_expiry_days.hint':
    'Used when inviting a user without a custom expiry.',
  'setting.security.password_reset_ttl_minutes': 'Password reset link TTL (minutes)',
  'setting.security.password_reset_ttl_minutes.hint': 'The link can only be used once.',
  'setting.security.geoip_enabled': 'Look up approximate location from IP',
  'setting.security.geoip_enabled.hint':
    'Shows the Location column under Sessions & devices. The signing-in IP is sent to ipwho.is over an encrypted connection; turn it off and nothing leaves the server. Results are approximate and can be hundreds of kilometres off.',
  'setting.security.trusted_device_days': 'Trusted device duration (days)',
  'setting.security.trusted_device_days.hint': 'Trusted devices skip the 2FA step.',
  'setting.sync.zone_interval_minutes': 'Zone sync interval (minutes)',
  'setting.sync.zone_interval_minutes.hint':
    'Syncs zone status AND all DNS records from Cloudflare. This is the regular record sync interval.',
  'setting.sync.token_check_interval_minutes': 'Token check interval (minutes)',
  'setting.sync.token_check_interval_minutes.hint':
    'Checks whether Cloudflare tokens are still valid.',
  'setting.sync.max_retries': 'Max retries',
  'setting.sync.max_retries.hint': 'Applied to each failed sync job.',
  'setting.sync.backoff_base_ms': 'Base backoff (ms)',
  'setting.sync.backoff_base_ms.hint':
    'When Cloudflare returns 429, the wait time grows from this value.',
  'setting.policy.require_admin_approval_for_domains': 'Require admin approval for new domains',
  'setting.policy.require_admin_approval_for_domains.hint':
    'New domains stay in pending state until approved before the zone is created on Cloudflare.',
  'setting.policy.max_domains_per_user': 'Domain limit per user',
  'setting.policy.max_domains_per_user.hint': 'Set to 0 for no limit.',
  'setting.policy.max_connections_per_user': 'Connection limit per user',
  'setting.policy.max_connections_per_user.hint': 'Set to 0 for no limit.',
  'setting.policy.allow_user_invites': 'Allow users to invite collaborators',
  'setting.policy.allow_user_invites.hint':
    'Disable if only administrators can share domain access.',

  'settings.signup.title': 'Public sign-up',
  'settings.signup.sub':
    'Strangers can create an account without an invitation. They only ever see domains they add themselves.',
  'setting.policy.allow_public_signup': 'Allow public sign-up',
  'setting.policy.allow_public_signup.hint':
    'Turning this off closes the sign-up page immediately and removes the link on the login screen. Existing accounts are unaffected.',
  'setting.security.signup_rate_limit': 'Maximum sign-ups per IP address',
  'setting.security.signup_rate_window_minutes': 'Time window for the limit (minutes)',
  'setting.security.signup_rate_limit.hint':
    'Counted within the window below. Once exceeded, that IP is refused until the window ends.',
  'setting.security.signup_rate_window_minutes.hint':
    'For example 3 sign-ups per 60 minutes. A longer window stops patient spam bots, but real users who mistype also wait longer.',
  'setting.security.turnstile_enabled': 'Enable Cloudflare Turnstile captcha',
  'setting.security.turnstile_enabled.hint':
    'Also stops spam bots that rotate IP addresses. Get both keys from Cloudflare Dashboard → Turnstile.',
  'setting.security.turnstile_site_key': 'Site key',
  'setting.security.turnstile_site_key.hint':
    'Public key — it ships inside the sign-up page, it is not a secret.',
  'setting.security.turnstile_secret_key': 'Secret key',
  'setting.security.turnstile_secret_key.hint':
    'Secret key — encrypted before storage and never shown again. Leave blank to keep the current one.',
  'setting.security.turnstile_missing_keys':
    'A key is still missing, so the captcha has NO effect yet. Sign-ups are accepted exactly as if it were off.',
  'setting.smtp.provider': 'Mail provider',
  'setting.smtp.provider.hint':
    'SMTP works with any provider. Resend sends over HTTPS — just an API key, no port or TLS config, not blocked by ISPs.',
  'setting.smtp.resendKey': 'Resend API key',
  'setting.smtp.resendKey.hint':
    'Get it at resend.com → API Keys. The sending domain must be verified on Resend, otherwise mail will be rejected.',
  'setting.smtp.host': 'Host',
  'setting.smtp.port': 'Port',
  'setting.smtp.user': 'Username',
  'setting.smtp.password': 'Password',
  'setting.smtp.password.hint':
    'Leave "***" unchanged to keep the existing value. Stored encrypted with AES-256-GCM.',
  'setting.smtp.tlsMismatch465':
    'Port 465 requires TLS from the start — ENABLE the toggle, otherwise the connection will hang until timeout.',
  'setting.smtp.tlsMismatchStarttls':
    'Port {port} uses STARTTLS — DISABLE the toggle, otherwise the server returns "wrong version number". Only port 465 should have TLS enabled.',
  'setting.smtp.secure': 'Secure connection (TLS)',
  'setting.smtp.secure.hint': 'Enable for port 465; disable for port 587 using STARTTLS.',
  'setting.smtp.from_email': 'Sender email',
  'setting.smtp.from_name': 'Sender name',
  'setting.branding.app_name': 'System name',
  'setting.branding.app_name.hint':
    'Shown in the sidebar, the top bar and in outgoing email. Leave empty to use DNSHub.',
  'setting.branding.tagline': 'Tagline',
  'setting.branding.tagline.hint':
    'The small line right below the system name. Leave empty to use the translation for the selected language.',
  'setting.branding.footer_text': 'Footer',
  'setting.branding.accent_color': 'Accent color',
  'setting.branding.accent_color.hint': 'CSS value, e.g. oklch(0.615 0.176 45).',
  'setting.branding.default_locale': 'Default language',
  'setting.branding.default_locale.hint': 'Applies to new accounts.',
  'setting.branding.default_timezone': 'Default timezone',
  'setting.branding.logo_light': 'Light theme logo',
  'setting.branding.logo_dark': 'Dark theme logo',
  'setting.branding.favicon': 'Favicon',

  // ── Nhật ký hoạt động · FR-130…135 ───────────────────────
  'audit.title': 'Activity log',
  'audit.ledeAdmin':
    'All actions on domains, users, and settings are logged with timestamp, actor, and IP address.',
  'audit.ledeUser': 'You are viewing your own activity log and that of the domains you own.',
  'audit.range': 'Time range',
  'audit.range.24h': 'Last 24 hours',
  'audit.range.7d': 'Last 7 days',
  'audit.range.30d': 'Last 30 days',
  'audit.range.custom': 'Custom…',
  'audit.from': 'From',
  'audit.to': 'To',
  'audit.export': 'Export log',
  'audit.system': 'System',
  'audit.expand': 'Show before/after details',
  'audit.stat.total': 'Events in range',
  'audit.stat.dns': 'DNS changes',
  'audit.stat.dnsSub': 'Added, updated, and deleted records',
  'audit.stat.security': 'Security events',
  'audit.stat.failures': 'failures',
  'audit.stat.noFailure': 'No failures',
  'audit.stat.permissions': 'Users & permissions',
  'audit.stat.permissionsSub': 'Invitations, role changes, and access grants',
  'audit.filter.all': 'All',
  'audit.filter.dns': 'DNS',
  'audit.filter.domain': 'Domain',
  'audit.filter.user': 'User',
  'audit.filter.security': 'Security',
  'audit.filter.system': 'System',
  'audit.searchPlaceholder': 'Search by actor, action, target, or IP address…',
  'audit.filterResultLabel': 'Filter by result',
  'audit.filterResultAll': 'All results',
  'audit.searchAria': 'Search events',
  'audit.col.at': 'When',
  'audit.col.actor': 'Actor',
  'audit.col.action': 'Action',
  'audit.col.target': 'Target',
  'audit.col.ip': 'IP address',
  'audit.col.result': 'Result',
  'audit.retention': 'Logs are append-only and retained for at least 365 days.',
  'audit.empty.title': 'No events found',
  'audit.empty.body': 'No log entries match the selected time range and filters.',
  'audit.attention.title': 'Needs attention',
  'audit.attention.sub': 'At-risk events within the current range',
  'audit.attention.empty': 'No warnings',
  'audit.distribution.title': 'Event distribution',
  'audit.detail.at': 'When',
  'audit.detail.target': 'Target',
  'audit.detail.userAgent': 'User agent',
  'audit.detail.requestId': 'Request ID',
  'audit.detail.before': 'Before',
  'audit.detail.after': 'After',

  // ── Phân trang dùng chung ────────────────────────────────
  'common.prev': 'Previous',
  'common.next': 'Next',
  'common.pageOf': 'Page {page}/{total}',
  'common.perPage': 'Rows per page',

  // ── M3 · Kết nối Cloudflare (FR-100…109) ─────────────────
  'conn.title': 'Cloudflare connections',
  'conn.ledeAdmin':
    'All Cloudflare connections in the system. Set one as the default — users without their own token will add domains to that Cloudflare account.',
  'conn.ledeUser':
    "Your connected Cloudflare accounts. When adding a domain, choose the connection to use; if you have none, the domain will be created on the system's default Cloudflare account.",
  'conn.add': 'Add connection',
  'conn.addFirst': 'Add your first connection',
  'conn.statConnections': 'Connections',
  'conn.statConnectionsAll': 'System-wide',
  'conn.statConnectionsMine': 'Added by you',
  'conn.statZones': 'Domains',
  'conn.statZonesNote': 'Managed via these connections',
  'conn.statBad': 'Needs attention',
  'conn.statBadNote': 'Token error or paused',
  'conn.statDefault': 'Default connection',
  'conn.statDefaultNote': 'Used when the user has no personal token',
  'conn.noDefault': 'Not set',
  'conn.noteAdmin':
    'Only one connection can be set as default at a time. Setting a different connection as default will automatically clear the current one.',
  'conn.noteUser':
    'You can only see and edit connections you added yourself. Tokens are encrypted; admins cannot read the original value.',
  'conn.listAll': 'All connections',
  'conn.listMine': 'My connections',
  'conn.listSubAll': 'Default connection is always first, then sorted by name',
  'conn.listSubMine': 'Select one of these connections when adding a new domain',
  'conn.searchPlaceholder': 'Search connections, Account ID, owner…',
  'conn.filter.all': 'All',
  'conn.filter.ok': 'Active',
  'conn.filter.invalid': 'Token error',
  'conn.filter.paused': 'Paused',
  'conn.col.name': 'Connection',
  'conn.col.owner': 'Owner',
  'conn.col.status': 'Status',
  'conn.col.domains': 'Domains',
  'conn.col.actions': 'Actions',
  'conn.domainsLink': 'View domains using “{name}”',
  'conn.footCount': '{count} connections',
  'conn.accountId': 'Account ID',
  'conn.accounts': 'Cloudflare accounts this token can reach',
  'conn.accountName': 'Account name',
  'conn.accountZones': 'Domains',
  'conn.accountPrimary': 'Default',
  'conn.accountsNone':
    'The token cannot read the account list. Check the token’s permissions on Cloudflare.',
  'conn.accountsHint':
    'Returned by Cloudflare — not editable here. The “Default” account is where new domains are created unless another account is chosen.',
  'conn.token': 'Token',
  'conn.checked': 'Last checked',
  'conn.never': 'Never checked',
  'conn.default': 'System default',
  'conn.setDefault': 'Set as default',
  'conn.verify': 'Check token',
  'conn.rotate': 'Replace',
  'conn.status.ok': 'Active',
  'conn.status.paused': 'Paused',
  'conn.status.invalid': 'Token error',
  'conn.emptyTitle': 'No Cloudflare connections yet',
  'conn.emptyBodyWithDefault':
    "Add your Cloudflare account's API token to manage DNS zones yourself. Until then, domains you add will be created on the system's default account — {name}.",
  'conn.emptyBodyNoDefault':
    "Add your Cloudflare account's API token to manage DNS zones yourself. No default connection is set, so you need to add a connection before adding any domains.",
  'conn.emptyFiltered': 'No connections match the filter.',
  'conn.newTitle': 'Add Cloudflare connection',
  'conn.newSub':
    'Create an API token on Cloudflare with Zone · DNS · Edit and Zone · Zone · Read permissions.',
  'conn.name': 'Label',
  'conn.namePlaceholder': 'E.g. Personal Cloudflare',
  'conn.apiToken': 'API token',
  'conn.apiTokenPlaceholder': 'Paste the token you just created on Cloudflare',
  'conn.apiTokenHint': 'The token is encrypted on save and only the last 4 characters are shown.',
  'conn.accountEmail': 'Account email',
  'conn.owner': 'Connection owner',
  'conn.ownerHint': 'Admins can add a connection on behalf of another user.',
  'conn.ownerSelf': '— Myself —',

  // Hướng dẫn lấy token — docs/06 §6.2
  'conn.guideShow': 'No token yet? View the guide',
  'conn.guideHide': 'Hide guide',
  'conn.guideTitle': 'Create an API token on Cloudflare',
  'conn.guideStep1': 'Sign in to Cloudflare and open',
  'conn.guideStep2':
    'Click “Create Token”, then select “Create Custom Token” at the bottom of the template list.',
  'conn.guideStep3':
    'Under Permissions, add all six entries below. Each missing entry disables the feature listed in the right column.',
  'conn.guideStep4':
    'Under Account Resources, select your account, then Continue → Create Token and copy the token. Cloudflare shows it only once.',
  'conn.guidePerm': 'Permission',
  'conn.guidePermUse': 'Used for',
  'dns.priorityShort': 'priority {n}',
  'ssl.daysLeft': '{n} days',
  'conn.guidePerm.zoneRead': 'List and read zones',
  'conn.guidePerm.zoneEdit': 'Add zones, change plan, pause, re-check nameservers',
  'conn.guidePerm.dnsEdit': 'Create, edit and delete DNS records; import and export',
  'conn.guidePerm.zoneSettings': 'SSL mode, minimum TLS, HSTS, Always Use HTTPS',
  'conn.guidePerm.sslRead': 'Read certificates and expiry dates',
  'conn.guidePerm.cachePurge': 'Purge cache',
  'conn.guidePerm.accountRead':
    'Retrieve account_id — required; without it you cannot add new domains',
  'conn.guidePerm.tunnel':
    'Create and configure Cloudflare Tunnel — only needed if you use this feature',
  'conn.guidePermOptional': 'Optional',
  'conn.guideSecurity':
    'The token is encrypted before saving and is never returned in full — only the last 4 characters are shown.',
  'conn.makeDefault': 'Set as system default connection',
  'conn.makeDefaultSub':
    'Domains added by users without their own token will be created on this account.',
  'conn.save': 'Save connection',
  'conn.editTitle': 'Edit connection',
  'conn.editSub': "{name}'s connection",
  'conn.currentToken': 'Current token',
  'conn.pause': 'Pause connection',
  'conn.pauseSub':
    'Keep the configuration but stop syncing and all DNS changes via this connection.',
  'conn.rotateTitle': 'Rotate API token',
  'conn.rotateSub': 'The old token stops working as soon as the new one is saved.',
  'conn.rotateWarn':
    'If the new token lacks permissions, syncing and all DNS changes will fail until you fix it.',
  'conn.rotateField': 'New Cloudflare token',
  'conn.rotateSave': 'Save new token',
  'conn.deleteTitle': 'Delete connection',
  'conn.deleteSub': 'Remove “{name}” from DNSHub.',
  'conn.deleteWarn':
    '{count} DNS zones are attached to this connection. After deletion, DNSHub will stop reading and writing those zones — records on Cloudflare remain unchanged.',
  'conn.deleteConfirm': 'Type the connection name to confirm',
  'conn.defaultTitle': 'Set system default connection',
  'conn.defaultConfirm':
    'From now on, domains for users without their own token will be created on “{name}”. The current default connection will be cleared.',
  'conn.created': 'Cloudflare connection added.',
  'conn.updated': 'Changes saved.',
  'conn.rotated': 'Cloudflare token replaced.',
  'conn.deleted': 'Connection deleted.',
  'conn.defaulted': '“{name}” set as the default connection.',
  'conn.verifiedOk': 'Connection “{name}” is valid · {count} DNS zones.',
  'conn.verifiedBad': 'Connection “{name}” failed to authenticate.',
  'conn.missingScopes': 'Token missing permissions: {list}',

  // ── M4 · Tên miền (FR-020…033) ───────────────────────────
  'domains.title': 'Domains',
  'domains.ledeAdmin':
    "All DNS zones are synced from Cloudflare. As an admin, you can see every user's domains.",
  'domains.ledeUser':
    "Only domains you added or were granted access to are shown. Other users' domains are not listed here.",
  'domains.filteredByConnection': 'Filtered: showing only domains using the “{name}” connection.',
  'domains.thisConnection': 'this connection',
  'domains.clearConnectionFilter': 'Clear filter',
  'domains.scopeNote': 'You are in personal scope. Domains you add are visible only to you.',
  'domains.add': 'Add domain',

  // Nhập tên miền có sẵn từ Cloudflare — OQ-115
  'domains.import': 'Import from Cloudflare',
  'domains.importTitle': 'Import existing domains from Cloudflare',
  'domains.importSub': 'Select DNS zones from your Cloudflare account to add to this system.',
  'domains.importConnection': 'From connection',
  'domains.importDomain': 'Domain',
  'domains.importStatus': 'Status',
  'domains.importPlan': 'Plan',
  'domains.importPick': 'Select',
  'domains.importAll': 'Select all ({n})',
  'domains.importNone': 'Deselect all',
  'domains.importSelected': '{n} domains selected',
  'domains.importAction': 'Import',
  'domains.importAlready': 'Already in system',
  'domains.importNoConn': '— No active Cloudflare connection —',
  'domains.importEmpty': 'No DNS zones found',
  'domains.importEmptyHint':
    'This connection has no DNS zones, or the token is missing the Zone → Zone → Read permission.',
  'domains.importScan': 'Scan DNS records',
  'domains.importScanSub': 'Fetch all records for each zone immediately after import.',
  'domains.importDone': 'Imported {n} domains and {r} DNS records.',
  // OQ-282 — nhập chạy nền, tiến độ hiện trên header.
  'domains.importQueued': 'Queued {n} zones. Watch progress in the top bar.',
  'domains.import.progress': 'Importing {done}/{total} zones from Cloudflare',
  'domains.import.progressAria': 'Cloudflare zone import progress',
  // OQ-283 — đối chiếu DNS sau khi gắn lại kết nối.
  'domains.import.progressReconcile': 'Reconciling DNS {done}/{total} domains',
  'reconcile.title': 'DNS reconciliation',
  'reconcile.sub':
    'Domains with differences between system data and Cloudflare, detected after you reconnected.',
  'reconcile.note':
    'Auto-sync is paused for the domains below. Once you resolve them, sync resumes as normal.',
  'reconcile.colDomain': 'Domain',
  'reconcile.colDbOnly': 'System only',
  'reconcile.colCfOnly': 'Cloudflare only',
  'reconcile.colDiffer': 'Value differs',
  'reconcile.colSince': 'Detected at',
  'reconcile.colActions': 'Actions',
  'reconcile.compare': 'Compare',
  'reconcile.skip': 'Skip',
  'reconcile.applyAll': 'Apply Cloudflare version to all',
  'reconcile.applyAllWarn':
    'All records from Cloudflare will be applied to {n} domains. Records that exist only in the system will be deleted.',
  'reconcile.applyAllHint':
    'Cloudflare is not modified. This action only overwrites the system-side data.',
  'reconcile.applied': 'Applied Cloudflare version to {n} domains.',
  'reconcile.appliedWithErrors': 'Applied to {n} domains, {e} failed — see Activity log.',
  'reconcile.emptyTitle': 'No domains need reconciliation',
  'reconcile.emptyHint': 'All domains match Cloudflare, or you have resolved all differences.',
  // OQ-286 — lịch sao lưu tự động.
  'settings.backupSchedule.title': 'Automatic backup schedule',
  'settings.backupSchedule.sub': 'The server creates backups on a schedule and removes old ones.',
  'settings.backupSchedule.enabled': 'Enable automatic backups',
  'settings.backupSchedule.enabledSub': 'Disabled: only manual backup via the button below.',
  'settings.backupSchedule.interval': 'Interval (hours)',
  'settings.backupSchedule.intervalHint': 'Default 24 hours. Minimum 1, maximum 168 (one week).',
  'settings.backupSchedule.keep': 'Versions to keep',
  'settings.backupSchedule.keepHint':
    'Older versions are deleted after each backup run. Default 7.',
  'settings.backupSchedule.note':
    'Backups are stored on the server itself. For off-site protection, also enable Remote backup storage below.',
  'settings.backupSchedule.saved': 'Backup schedule saved.',

  // OQ-286 — hướng dẫn lấy khoá cho kho sao lưu ngoài.
  'domains.importCompare': 'Compare DNS',
  'domains.importExists': 'Previously added — compare before re-importing',
  'domains.reimportTitle': 'Compare DNS · {name}',
  'domains.reimportSub':
    'This domain was previously added. Choose which side to keep for each record. Applying only creates or updates on Cloudflare — it never deletes.',
  'domains.reimportRecord': 'Record',
  'domains.reimportState': 'Status',
  'domains.reimportDb': 'In system (DB)',
  'domains.reimportCf': 'On Cloudflare',
  'domains.reimportChoice': 'Keep',
  'domains.reimportState.same': 'Identical',
  'domains.reimportState.differ': 'Different value',
  'domains.reimportState.db_only': 'DB only',
  'domains.reimportState.cf_only': 'Cloudflare only',
  'domains.reimportKeep': 'Keep',
  'domains.reimportDrop': 'Skip',
  'domains.reimportUseCf': 'Use Cloudflare',
  'domains.reimportUseDb': 'Use DB',
  'domains.reimportApply': 'Apply',
  'domains.reimportCount': '{n} records',
  'domains.reimportEmpty': 'No records to compare',
  'domains.reimportEmptyHint': 'Both sides are identical or there are no records yet.',
  'domains.reimportDone':
    'Created {c}, updated {u} on Cloudflare · imported {i}, dropped {d} from DB.',
  'domains.sync': 'Sync with Cloudflare',
  'domains.statTotal': 'Total domains',
  'domains.statActive': 'Active',
  'domains.statActiveNote': 'Nameservers pointing correctly',
  'domains.statPending': 'Pending nameservers',
  'domains.statPendingNote': 'Point nameservers to activate',
  'domains.statPaused': 'Paused',
  'domains.statPausedNote': 'Not serving through Cloudflare',
  'domains.statTotalNote': 'Within your scope',
  'domains.filterAll': 'All',
  'domains.filterActive': 'Active',
  'domains.filterPending': 'Pending NS',
  'domains.filterPaused': 'Paused',
  'domains.filterSyncPaused': 'Sync disabled',
  'domains.filterError': 'Error',
  'domains.searchPlaceholder': 'Search domains, Cloudflare connections…',
  'domains.searchPlaceholderAdmin': 'Search domains, owners, Cloudflare connections…',
  'domains.filterPlanLabel': 'Filter by plan',
  'domains.filterPlanAll': 'All plans',
  'domains.colName': 'Domain',
  'domains.colOwner': 'Owner',
  'domains.colStatus': 'Status',
  'domains.colPlan': 'Plan',
  'domains.colRecords': 'Records',
  'domains.colShares': 'Shared',
  'share.cell.title': '{n} people hold access · {p} invitations pending',
  'share.cell.pendingShort': '+{p} pending',
  'domains.colConnection': 'Cloudflare connection',
  'domains.colUpdated': 'Updated',
  'domains.colActions': 'Actions',
  'domains.open': 'Open',
  'domains.page': 'Page {page} · {total} domains',
  'domains.emptyTitle': 'No domains yet',
  'domains.emptyBody': 'Add your first domain to start managing DNS zones.',
  'domains.emptyFilterTitle': 'No domains match the filter',
  'domains.emptyFilterBody': 'Try removing some filters or clearing the search keyword.',
  'domains.emptyScopeTitle': 'You don’t have access to any domains',
  'domains.emptyScopeBody': 'Domains appear here only when you add them or are granted access.',
  'domains.status.active': 'Active',
  'domains.status.pending': 'Pending nameservers',
  'domains.status.paused': 'Paused',
  'domains.status.moved': 'Moved',
  'domains.status.error': 'Error',
  'domains.relation.owned': 'Added by you',
  'domains.relation.granted': 'Granted',
  'domains.addTitle': 'Add domain',
  'domains.addSub': 'A DNS zone will be created on Cloudflare and assigned to your account.',
  'domains.nameLabel': 'Root domain',
  'domains.namePlaceholder': 'example.com',
  'domains.nameHint': 'Enter the root domain without http:// or www.',
  'domains.planLabel': 'Cloudflare plan',
  'domains.assignLabel': 'Assign to user',
  'domains.assignSelf': '— Myself —',
  'domains.assignHint':
    'Admins can add a domain on behalf of another user; the connection list filters to that user.',
  'domains.cfAccountLabel': 'Cloudflare account',
  'domains.cfAccountHint':
    'This connection’s token reaches several accounts — pick the one that will hold the DNS zone.',
  'domains.cfAccountNsHint':
    'Existing domains on this account mostly use {ns}. The official nameservers for the new domain appear in step 2.',
  'domains.cfAccountZones': '{n} domains',
  'domains.nsHintAll':
    'All {n} existing domains on “{name}” use this nameserver pair. Cloudflare confirms the official values in step 2.',
  'domains.nsHintMost':
    '{n} of {tong} domains on “{name}” use this pair — Cloudflare assigns per zone, so yours may differ. Take the official values from step 2 before changing them at your registrar.',
  'domains.nsUnknown':
    'This account has no domains yet, so the nameservers cannot be predicted. Cloudflare returns them right after creation, in step 2.',
  'domains.connectionLabel': 'Cloudflare connection',
  'domains.connectionHintOne': 'The domain will be created on this account’s only connection.',
  'domains.connectionHintOneSelf': 'The domain will be created on your only connection.',
  'domains.connectionHintMany':
    'Choose the Cloudflare account that will hold this domain’s DNS zone.',
  'domains.connectionHintNone':
    'No personal connection — using the system’s default Cloudflare account.',
  'domains.connectionFallback':
    'This user has no personal Cloudflare connection. The DNS zone will be created on the system default account — {name}.',
  'domains.connectionFallbackSelf':
    'You have no personal Cloudflare connection yet. The DNS zone will be created on the system default account — {name}.',
  'domains.connectionNone':
    'No usable Cloudflare connection. Add a personal connection or ask an admin to set a system default.',
  'domains.connectionAdd': 'Add personal connection',
  'domains.scanRecords': 'Import existing DNS records',
  'domains.scanRecordsSub': 'Scan the domain’s active records before switching nameservers.',
  'domains.addStep': 'Step 1/2 · Nameserver verification in the next step',
  'domains.nsTitle': 'Point nameservers to activate',
  'domains.nsSub':
    'Go to your registrar’s domain management page and replace the current nameservers with the two values below.',
  'domains.nsLabel': 'Nameservers to set',
  'domains.nsRecheck': 'Re-check',
  'domains.nsPending': 'Nameservers not yet pointing correctly. Updates can take up to 24 hours.',
  'domains.nsActive': 'Domain is active on Cloudflare.',
  'domains.created': 'Added {name}. Point the nameservers to activate.',
  'domains.synced': 'Synced: added {added}, updated {updated}, removed {removed} records.',
  'domains.deleteTitle': 'Remove domain from system',
  'domains.deleteWarn':
    'DNSHub will stop reading and writing this DNS zone. The zone on Cloudflare remains intact — you can add it again later.',
  'domains.deleteConfirm': 'Type the domain name to confirm',
  'domains.deleted': 'Domain removed from system.',

  // ── M4 · Chi tiết tên miền (FR-030…045) ──────────────────
  'detail.back': '← Domains',
  'detail.switch': 'Switch domain',
  'detail.switchSearch': 'Search your domains…',
  'detail.switchAll': 'View all domains',
  'detail.switchEmpty': 'No domains match.',
  'detail.lede': 'DNS zone synced with Cloudflare {when} · {count} records · owner {owner}.',
  'detail.ledeNoSync': 'DNS zone not synced · {count} records.',
  'detail.export': 'Export zone file',
  'detail.import': 'Import zone file',
  'detail.purge': 'Purge cache',
  'detail.addRecord': 'Add record',
  'detail.tab.dns': 'DNS records',
  'detail.tab.overview': 'Overview',
  'detail.tab.ssl': 'SSL/TLS',
  'detail.tab.access': 'Access',
  'detail.tab.log': 'Activity log',
  'detail.lockedPending':
    'This domain is not active yet because the nameservers do not point to Cloudflare. Point them at your registrar to the two values below, then press “Re-check”. You can still set up DNS records now — Cloudflare picks them up as soon as the nameservers switch.',
  'detail.lockedMoved':
    'Cloudflare reports that this domain no longer points at their nameservers, so DNS changes have no effect until it points back. Use the two values below, then press “Re-check”.',
  'detail.zoneInfo': 'Zone info',
  'detail.zoneId': 'Zone ID',
  'detail.owner': 'Owner',
  'detail.connection': 'Cloudflare connection',
  'detail.cfAccount': 'Cloudflare account',
  'detail.plan': 'Plan',
  'detail.createdAt': 'Added on',
  'detail.lastSync': 'Last synced',
  'detail.nameservers': 'Nameservers',
  'detail.originalNs': 'Original nameservers',
  'detail.danger': 'Danger zone',
  'detail.pause': 'Pause Cloudflare',
  'detail.pausedReasonLabel': 'Pause reason',
  'detail.syncPausedLabel': 'Sync',
  'detail.syncPause': 'Pause sync',
  'detail.syncResume': 'Resume sync',
  'detail.syncPaused': 'Sync disabled',
  'detail.syncPausedHint':
    'DNSHub no longer reads or writes this domain. Cloudflare is unchanged — the website keeps running normally.',
  'detail.syncPauseConfirm':
    'Pause sync for {name}? DNSHub will stop reading and writing this domain: DNS records cannot be edited here and background jobs will skip it.',
  'detail.syncPauseUndo':
    'Your website is NOT affected — Cloudflare keeps everything as-is. Resume at any time.',
  'detail.syncPausedDone': 'Sync paused for {name}.',
  'detail.syncResumedDone': 'Sync resumed for {name}.',
  'detail.pausedReason.manual': 'You paused this domain on Cloudflare.',
  'detail.pausedReason.orphaned': 'No Cloudflare connection available — reconnect one to continue.',
  'detail.pausedReason.zone_missing':
    'This zone no longer exists on Cloudflare. Reconnecting a connection will not help.',
  'detail.pausedReason.cloudflare':
    'This domain was paused from the Cloudflare dashboard, not from DNSHub.',
  'detail.syncPausedReason.manual': 'You manually paused sync for this domain.',
  'detail.syncPausedReason.connection':
    'The Cloudflare connection is suspended, so all its domains have sync disabled.',
  'detail.pauseConfirm':
    'Pause Cloudflare for {name}? Traffic will go directly to the origin server — CDN, WAF, and cache stop working, and your origin IP is exposed.',
  'detail.pauseUndo':
    'Re-enable at any time using the "Resume Cloudflare" button. DNS records are unchanged.',
  'detail.resumeConfirm':
    'Resume Cloudflare for {name}? Traffic will flow through the Cloudflare network again.',
  'detail.resumeUndo': 'Takes effect within seconds via anycast.',
  'roles.resetConfirm': 'Reset role {name} to defaults? All customized capabilities will be lost.',
  'roles.resetUndo': 'This cannot be undone — you will need to reselect each capability.',
  'profile.sessions.endOthersConfirm':
    'Sign out all other devices? Sessions open on other machines will be ended immediately.',
  'profile.sessions.endOthersUndo':
    'Your current session is not affected. Other devices will need to sign in again.',
  'detail.resume': 'Resume Cloudflare',
  'detail.remove': 'Remove domain from system',
  'detail.paused': 'Cloudflare paused for {name}.',
  'detail.resumed': 'Cloudflare resumed for {name}.',
  'detail.purged': 'Cache purge request sent.',
  'detail.notAllowed': 'You don’t have permission to perform this action on this domain.',

  // ── M4 · Bản ghi DNS (FR-034…041) ────────────────────────
  'dns.colType': 'Type',
  'dns.colName': 'Name',
  'dns.colContent': 'Value',
  'dns.colProxy': 'Proxy',
  'dns.colTtl': 'TTL',
  'dns.colUpdated': 'Updated',
  'dns.searchPlaceholder': 'Search by name, value, or comment…',
  'dns.notProxiable': 'Not proxiable',
  'dns.ttlAuto': 'Auto',
  'dns.emptyTitle': 'No DNS records yet',
  'dns.emptyBody': 'Add your first record or sync from Cloudflare.',
  'dns.emptyFilter': 'No records match the current filter.',
  'dns.recordTitle': 'DNS record',
  'dns.recordSub': '{name} · changes take effect almost instantly',
  'dns.type': 'Type',
  'dns.name': 'Name',
  'dns.namePlaceholder': 'www or @ for the root domain',
  'dns.content': 'Value',
  'dns.contentHint': 'For CNAME, enter the target domain, e.g. app.vercel.app',
  'dns.ttl': 'TTL',
  'dns.priority': 'Priority',
  'dns.proxy': 'Proxy through Cloudflare',
  'dns.proxySub':
    'Hides the origin IP and enables CDN and WAF for this record. TTL is set to Auto.',
  'dns.comment': 'Comment',
  'dns.commentHint':
    'This comment is stored on Cloudflare with the record — anyone viewing it there can read it.',
  'dns.commentPlaceholder': 'e.g. points to Hanoi web cluster',
  'dns.srvService': 'Service',
  'dns.srvProto': 'Proto',
  'dns.srvWeight': 'Weight',
  'dns.srvPort': 'Port',
  'dns.srvTarget': 'Target',
  'dns.caaFlags': 'Flags',
  'dns.caaTag': 'Tag',
  'dns.caaValue': 'Value',
  'dns.save': 'Save record',
  'dns.created': 'DNS record added.',
  'dns.updated': 'DNS record saved.',
  'dns.deleted': 'DNS record deleted.',
  'dns.deletedUndo': '{name} deleted. Undo within 10 seconds.',
  'dns.deleteTitle': 'Confirm deletion',
  'dns.deleteSub': 'This action cannot be undone.',
  'dns.deleteWarn': 'Services using this record may go down for a few minutes.',
  'dns.deleteConfirmLabel': 'Delete {type} record {name}?',
  'dns.proxyOn': 'Proxy enabled for {name}.',
  'dns.proxyOff': 'Proxy disabled for {name}.',
  'dns.footNote': "Record changes take effect within seconds via Cloudflare's anycast network.",

  // ── M4 · SSL/TLS (FR-042) ────────────────────────────────
  'ssl.note':
    'Universal SSL certificates are issued by Cloudflare and automatically renewed 30 days before expiry.',
  'ssl.modeTitle': 'Encryption mode',
  'ssl.modeSub': 'How Cloudflare connects to your origin server',
  'ssl.modeLabel': 'Current mode',
  'ssl.mode.off': 'Off',
  'ssl.mode.flexible': 'Flexible',
  'ssl.mode.full': 'Full',
  'ssl.mode.strict': 'Full (strict)',
  'ssl.mode.offSub': 'No encryption between the browser and Cloudflare.',
  'ssl.mode.flexibleSub': 'Encrypted to Cloudflare, unencrypted to the origin server.',
  'ssl.mode.fullSub': 'End-to-end encryption; self-signed certificates are accepted at the origin.',
  'ssl.mode.strictSub':
    'End-to-end encryption with a valid certificate required at the origin. Recommended for production.',
  'ssl.alwaysHttps': 'Always Use HTTPS',
  'ssl.alwaysHttpsSub': 'Redirects all HTTP requests to HTTPS.',
  'ssl.hsts': 'HSTS',
  'ssl.hstsSub': 'Forces browsers to access the site over HTTPS for the configured duration.',
  'ssl.hstsMaxAge': 'max-age (seconds)',
  'ssl.hstsSubdomains': 'Include subdomains',
  'ssl.hstsPreload': 'Submit to preload list',
  'ssl.minTls': 'Minimum TLS Version',
  'ssl.minTlsSub': 'Rejects connections using older protocol versions.',
  'ssl.certTitle': 'Active certificate',
  'ssl.certExpires': 'Expires',
  'ssl.certNone': 'Certificate information unavailable. Try syncing again.',
  'ssl.saved': 'SSL/TLS configuration saved.',
  'ssl.purgeTitle': 'Purge Cloudflare cache',
  'ssl.purgeAll': 'Purge all',
  'ssl.purgeAllSub': 'All static assets will be reloaded from the origin server.',
  'ssl.purgeFiles': 'Or enter a list of paths, one URL per line',

  // ── M4 · Nhập zone file (FR-039) ─────────────────────────
  'import.title': 'Import zone file (BIND)',
  'import.sub': 'Paste the zone file contents. The system shows a preview before applying.',
  'import.content': 'Zone file contents',
  'import.preview': 'Preview',
  'import.apply': 'Apply changes',
  'import.summary': 'Add {create} · Update {update} · Skip {skip}',
  'import.skippedLines': '{count} unreadable lines',
  'import.action.create': 'Add',
  'import.action.update': 'Update',
  'import.action.skip': 'Skip',
  'import.applied': 'Imported: {created} added, {updated} updated.',
  'import.failed': '{count} records could not be applied.',

  /* ── Cloudflare Tunnel — OQ-337 ─────────────────────────────────── */
  'tunnel.button': 'Tunnel',
  'tunnel.title': 'Cloudflare Tunnel',
  'tunnel.subtitle': 'Connection {name}',
  'tunnel.intro':
    'A tunnel lets your server receive traffic through Cloudflare without opening ports or having a static IP. This system creates and configures tunnels; you run cloudflared on your own server.',
  'tunnel.newName': 'New tunnel name',
  'tunnel.newNamePlaceholder': 'example-web-server',
  'tunnel.create': 'Create tunnel',
  'tunnel.created': 'Tunnel created.',
  'tunnel.deleted': 'Tunnel deleted.',
  'tunnel.emptyTitle': 'No tunnels yet',
  'tunnel.emptyHint': 'Create your first tunnel to expose an internal service through Cloudflare.',
  'tunnel.colName': 'Name',
  'tunnel.colStatus': 'Status',
  'tunnel.colConns': 'Connected machines',
  'tunnel.colHostname': 'Hostname',
  'tunnel.colService': 'Internal service',
  'tunnel.actions': 'Actions',
  'tunnel.status.inactive': 'Not running',
  'tunnel.status.healthy': 'Running',
  'tunnel.status.degraded': 'Degraded',
  'tunnel.status.down': 'Down',
  'tunnel.local': 'Locally configured',
  'tunnel.localHint':
    'This tunnel is configured by a file on the machine running cloudflared, so it cannot be edited here. Edit config.yml on that machine.',
  'tunnel.setup': 'Details',
  'tunnel.setupTitle': 'Set up tunnel {name}',
  'tunnel.inactiveNote':
    'The tunnel exists on Cloudflare but NO machine is running it yet. It cannot serve traffic until you finish step 1.',
  'tunnel.step1': 'Step 1 — Run cloudflared on your server',
  'tunnel.step1Hint':
    'Get the install command and paste it on the server that will run the tunnel.',
  'tunnel.showToken': 'Get install command',
  'tunnel.tokenWarn':
    'The command below contains a token — anyone holding it can turn their machine into an exit for this tunnel. Only paste it on your own server; never send it over chat or email.',
  'tunnel.dockerAlt': 'Or run it with Docker:',
  'tunnel.step2': 'Step 2 — Point your hostname',
  'tunnel.step2Hint':
    'Add a CNAME record pointing to {target} with proxy enabled (orange cloud) on the Domains page.',
  'tunnel.step3': 'Step 3 — Declare internal services',
  'tunnel.addRule': 'Add row',
  'tunnel.configSaved': 'Tunnel configuration saved.',
  'tunnel.advancedCount': '{count} advanced options',
  'tunnel.advancedHint':
    'This row has advanced settings configured on Cloudflare. Click to view — they are preserved when you save.',
  'tunnel.advancedTitle': 'Advanced options',
  'tunnel.advancedReadOnly':
    'These options live on Cloudflare and are read-only here. They are preserved when you save; to edit them go to Cloudflare → Zero Trust → Networks → Tunnels.',
  'tunnel.advancedCol': 'Option',
  'tunnel.advancedVal': 'Value',
  'tunnel.advancedAll':
    'This tunnel has {count} advanced options applied to every row, configured on Cloudflare. They are preserved when you save.',
};
